import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Edit, Mail, Phone, MapPin, Linkedin, Globe, Briefcase, GraduationCap, Award, Code, Share2, Download, Languages, Image, Upload, Save, X, Camera, Palette, User, Plus, Eye, Search, Users, Building2, Lock, MessageSquare, BookOpen, ExternalLink, MoreHorizontal, Send, Database, UserPlus, Flag, Info, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import resumeAPI, { generatePublicLinkId } from '../services/api';
import { messagingAPI } from '../services/messaging';
import { networkAPI } from '../services/network';
import CredlyBadges from '../components/CredlyBadges';
import ProfilePictureFrame from '../components/ProfilePictureFrame';
import ImageEditorModal from '../components/ImageEditorModal';
import { HIGHLIGHT_COLORS } from '../config/constants';
import ProfileViewsCard from '../components/analytics/ProfileViewsCard';
import SearchAppearancesCard from '../components/analytics/SearchAppearancesCard';
import ViewerConnectionsCard from '../components/analytics/ViewerConnectionsCard';
import CompanyColleaguesCard from '../components/analytics/CompanyColleaguesCard';

const ProfilePage = ({ forcedReadOnly = false, publicSlugOverride = null, onAuthAction } = {}) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { publicLinkId: publicLinkIdParam, profileSlug: profileSlugParam } = useParams();
  const [searchParams] = useSearchParams();
  const publicLinkIdQuery = searchParams.get('public') || searchParams.get('p');
  const profileUserIdQuery = searchParams.get('uid') || searchParams.get('user');
  const resumeIdQuery = searchParams.get('rid') || searchParams.get('resume') || searchParams.get('resume_id');
  const previewRaw = (searchParams.get('preview') || searchParams.get('readonly') || '').trim().toLowerCase();
  const forceReadOnly = forcedReadOnly || previewRaw === '1' || previewRaw === 'true' || previewRaw === 'yes';
  const uidParam = (profileUserIdQuery || '').trim();
  const resumeIdParam = (resumeIdQuery || '').trim();
  const publicParam = (publicLinkIdParam || publicLinkIdQuery || '').trim();
  const publicSlugParam = (publicSlugOverride || profileSlugParam || '').trim();
  const viewerId = user?.user_id || user?.userId || user?.id || user?._id || null;

  // Identity resolution (pre-draft/publish change):
  // 1) uid exists -> profile_user_id = uid
  // 2) else if public exists -> resolveFromPublicLink(public)
  // 3) else -> profile_user_id = viewer_id
  const [resolvedProfileUserId, setResolvedProfileUserId] = useState(uidParam || null);
  const [resolvedFromPublic, setResolvedFromPublic] = useState(null);

  // "View profile (read-only)" for the signed-in user should still load via the normal
  // authenticated path (same as /profile), but with editing disabled.
  const isSelfPreviewRoute =
    !!forceReadOnly &&
    !publicParam &&
    !publicSlugParam &&
    !uidParam &&
    !resumeIdParam &&
    !!viewerId;
  const isPublicProfileView = (!!uidParam || !!publicParam || !!publicSlugParam || !!resumeIdParam) && !isSelfPreviewRoute;
  const protectedEmail = 'benjamin.maderajr@gmail.com';
  const isProtectedAccount = ((user?.email || '').trim().toLowerCase() === protectedEmail);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);
  const [credlyBadges, setCredlyBadges] = useState([]);
  const [editingImages, setEditingImages] = useState(false);
  const [tempPhotoUrl, setTempPhotoUrl] = useState('');
  const [tempBannerUrl, setTempBannerUrl] = useState('');
  const [tempHighlightColor, setTempHighlightColor] = useState('');
  const [tempProfileFrame, setTempProfileFrame] = useState('');
  const [customFrameText, setCustomFrameText] = useState('');
  const [savingImages, setSavingImages] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState({ expIndex: null });
  const [uploadingEduLogo, setUploadingEduLogo] = useState({ eduIndex: null });
  const [uploadingCertLogo, setUploadingCertLogo] = useState({ certIndex: null });
  const [expandedExperiences, setExpandedExperiences] = useState({});
  const [expandedSkills, setExpandedSkills] = useState({});
  const [editingExperience, setEditingExperience] = useState(null); // Index of experience being edited
  const [editingExpData, setEditingExpData] = useState(null); // Copy of experience data being edited
  const [savingExperience, setSavingExperience] = useState(false);
  const [editingAbout, setEditingAbout] = useState(false);
  const [editingProfessionalSummary, setEditingProfessionalSummary] = useState(false);
  const [editingAboutText, setEditingAboutText] = useState('');
  const [editingProfessionalSummaryText, setEditingProfessionalSummaryText] = useState('');
  const [savingAbout, setSavingAbout] = useState(false);
  const [savingProfessionalSummary, setSavingProfessionalSummary] = useState(false);
  const [expandedEducation, setExpandedEducation] = useState({});
  const [editingEducation, setEditingEducation] = useState(null);
  const [editingEduData, setEditingEduData] = useState(null);
  const [savingEducation, setSavingEducation] = useState(false);
  const [editingCertification, setEditingCertification] = useState(null);
  const [editingCertData, setEditingCertData] = useState(null);
  const [savingCertification, setSavingCertification] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [recoveringProfile, setRecoveringProfile] = useState(false);
  const [recoveryNote, setRecoveryNote] = useState('');
  const [viewDebug, setViewDebug] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [savingToUsersTable, setSavingToUsersTable] = useState(false);
  const [isConnectedWithProfileUser, setIsConnectedWithProfileUser] = useState(false); // wired to network API in frontend step
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editingImageType, setEditingImageType] = useState(null); // 'photo' or 'banner'
  const profileOwnerId = profile?.owner_user_id || profile?.user_id || null;
  const [unreadMessagingCount, setUnreadMessagingCount] = useState(0);
  
  // Get view source from URL (e.g., ?source=find_people)
  const viewSource = searchParams.get('source') || 'direct';

  // Analytics API base URL (fallback to default if env not set)
  const analyticsApiBaseUrl =
    (import.meta?.env?.VITE_API_BASE_URL || '').trim() ||
    'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';

  // Shared token lookup for analytics requests
  const getAnalyticsToken = () => {
    try {
      const localUser = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
      return (
        user?.tokens?.idToken ||
        user?.idToken ||
        localUser?.tokens?.idToken ||
        localUser?.idToken ||
        sessionUser?.tokens?.idToken ||
        sessionUser?.idToken ||
        ''
      );
    } catch {
      return user?.tokens?.idToken || user?.idToken || '';
    }
  };

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState({
    profileViews: { views: [], count: 0, days: 7 },
    searchAppearances: { count: 0, topQueries: [], days: 7 },
    viewerConnections: { connections: [], count: 0 },
    companyColleagues: { colleagues: [], count: 0, company: '' },
  });
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Debug mode for analytics
  const debugAnalytics = searchParams.get('debug') === 'analytics';
  const [debugLogs, setDebugLogs] = useState([]);
  const addDebugLog = (message, type = 'info') => {
    if (!debugAnalytics) return;
    setDebugLogs(prev => [...prev, { 
      time: new Date().toLocaleTimeString(), 
      message, 
      type 
    }].slice(-20)); // Keep last 20 logs
  };

  const updateMetaTag = (attr, key, content) => {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  useEffect(() => {
    if (!publicSlugParam || !profile) return;
    const name = profile.title || profile.name || '';
    const headline = profile.headline || '';
    const description = headline || profile.professional_summary || profile.summary || '';
    if (name) {
      document.title = `${name} | Career Branding Network`;
    }
    updateMetaTag('name', 'description', description);
    updateMetaTag('property', 'og:title', name ? `${name} | Career Branding Network` : 'Career Branding Network');
    updateMetaTag('property', 'og:description', description);
    updateMetaTag('property', 'og:type', 'profile');
    if (profile.photo_url) {
      updateMetaTag('property', 'og:image', profile.photo_url);
    }
  }, [publicSlugParam, profile]);

  // Helper to add cache-busting to CloudFront URLs
  // Use a stable token to avoid flicker on re-render
  const addCacheBust = (url, token) => {
    if (!url) return '';
    if (typeof url !== 'string') return url;
    // Only add cache-bust to CloudFront URLs
    if (url.includes('cloudfront.net')) {
      try {
        const urlObj = new URL(url);
        if (token) urlObj.searchParams.set('v', token);
        return urlObj.toString();
      } catch {
        return token ? `${url}?v=${token}` : url;
      }
    }
    return url;
  };

  const photoCacheToken = useMemo(() => (profile?.photo_url ? String(Date.now()) : ''), [profile?.photo_url]);
  const bannerCacheToken = useMemo(() => (profile?.banner_url ? String(Date.now()) : ''), [profile?.banner_url]);
  const cachedPhotoUrl = addCacheBust(profile?.photo_url, photoCacheToken);
  const cachedBannerUrl = addCacheBust(profile?.banner_url, bannerCacheToken);

  // Helper to get preview URL from Blob, data URL, or HTTP URL
  const getPreviewUrl = (imageData) => {
    if (!imageData) return '';
    if (imageData instanceof Blob) {
      return URL.createObjectURL(imageData);
    }
    return addCacheBust(imageData);
  };

  // Clean up object URLs on unmount to avoid memory leaks
  React.useEffect(() => {
    return () => {
      if (tempPhotoUrl instanceof Blob) URL.revokeObjectURL(URL.createObjectURL(tempPhotoUrl));
      if (tempBannerUrl instanceof Blob) URL.revokeObjectURL(URL.createObjectURL(tempBannerUrl));
    };
  }, [tempPhotoUrl, tempBannerUrl]);

  const cacheProfileView = (key, value) => {
    if (!key || !value) return;
    const payload = { at: Date.now(), value };
    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
    } catch {
      // ignore
    }
    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch {
      // ignore
    }
  };

  const readProfileViewCache = (key, maxAgeMs = 5 * 60 * 1000) => {
    if (!key) return null;
    const tryParse = (raw) => {
      if (!raw) return null;
      try {
        const obj = JSON.parse(raw);
        if (!obj || typeof obj !== 'object') return null;
        if (typeof obj.at !== 'number' || !obj.value) return null;
        if (Date.now() - obj.at > maxAgeMs) return null;
        return obj.value;
      } catch {
        return null;
      }
    };
    // Prefer sessionStorage (tab-scoped), then localStorage (cross-tab fallback).
    return tryParse(sessionStorage.getItem(key)) || tryParse(localStorage.getItem(key)) || null;
  };

  const notifySaveResult = (result, successMessage) => {
    const localOnly = result && result.__save_status === 'local_only';
    if (localOnly) {
      // Option A: skip the alert entirely so user never sees "Saved locally" wording.
      return;
    }
    if (successMessage) alert(successMessage);
  };

  // Read-only / editability
  const currentUserId = viewerId;
  const currentEmail = (user?.email || '').trim().toLowerCase();
  const profileEmail = (profile?.contact_email || profile?.user_email || profile?.email || '').trim().toLowerCase();
  const effectiveProfileUserId = resolvedProfileUserId || resolvedFromPublic || viewerId || null;
  const editable = !!viewerId && !!effectiveProfileUserId && viewerId === effectiveProfileUserId && !forceReadOnly;
  const isOwnProfile =
    !profile
      ? true
      : editable ||
        (!!currentUserId &&
          (profileOwnerId ? profileOwnerId === currentUserId : (profileEmail ? profileEmail === currentEmail : true)));
  const canEditProfile = isAuthenticated && editable && !isPublicProfileView;
  const canAttemptRecovery = canEditProfile && !isProtectedAccount;
  const isReadOnlyOtherProfile = isAuthenticated && !canEditProfile && isPublicProfileView;
  const showPublicActions = isPublicProfileView && !canEditProfile && !isOwnProfile;
  // Globally public requirement:
  // - Allow Find People to be used even when logged out.
  // - Keep owner-only widgets behind canEditProfile.
  const showOwnerOnlyDashboard = canEditProfile && !forceReadOnly;

  useEffect(() => {
    // Redirect legacy public_link_id URLs to /in/:profileSlug
    const isOnViewRoute =
      location.pathname === '/profile/view' ||
      location.pathname.startsWith('/profile/view') ||
      location.pathname.startsWith('/profile/public');

    if (publicSlugParam) {
      loadProfile();
      return;
    }

    if (publicParam && isOnViewRoute) {
      const redirectToSlug = async () => {
        try {
          const slug = await resumeAPI.resolveProfileSlugFromPublicLink?.(publicParam);
          if (slug) {
            navigate(`/in/${encodeURIComponent(slug)}`, { replace: true });
            return;
          }
        } catch (e) {
          console.warn('Failed to resolve profile slug:', e);
        }
        loadProfile();
      };
      redirectToSlug();
      return;
    }

    // If viewing own profile via /profile/view without public_link_id parameter, redirect to /in/:profileSlug
    if (isAuthenticated && isOnViewRoute && !isPublicProfileView && !publicParam && !publicSlugParam && !uidParam && !resumeIdParam) {
      const redirectToPublicSlug = async () => {
        try {
          const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim();
          if (AWS_API_BASE_URL) {
            const getAwsIdToken = () => {
              try {
                const u = JSON.parse(localStorage.getItem('user') || '{}');
                return u?.tokens?.idToken || u?.idToken || '';
              } catch {
                return '';
              }
            };
            const token = getAwsIdToken();
            const awsUrl = (path) => {
              const base = AWS_API_BASE_URL.replace(/\/+$/, '');
              const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
              return `${base}${p}`;
            };
            const res = await fetch(awsUrl('/me/profile'), {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
              }
            });
            if (res.ok) {
              const data = await res.json();
              const slug = data?.profile?.profile_slug || null;
              const pid = data?.profile?.public_link_id || null;
              if (slug) {
                navigate(`/in/${encodeURIComponent(slug)}`, { replace: true });
                return;
              }
              if (pid) {
                navigate(`/profile/view?public=${encodeURIComponent(pid)}`, { replace: true });
                return;
              }
            }
          }
        } catch (e) {
          console.warn('Failed to redirect to public slug:', e);
        }
        // If redirect fails, continue with normal load
        loadProfile();
      };
      redirectToPublicSlug();
    } else {
      loadProfile();
    }
  }, [isAuthenticated, navigate, uidParam, publicParam, publicSlugParam, resumeIdParam, viewerId, isPublicProfileView, location.pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Rule 1: uid wins, always
        if (uidParam) {
          setResolvedProfileUserId(uidParam);
          setResolvedFromPublic(null);
          return;
        }
        // Rule 2: resolve from public link
        if (publicParam) {
          const resolved = await resumeAPI.resolveProfileUserIdFromPublicLink(publicParam);
          if (cancelled) return;
          setResolvedFromPublic(resolved);
          setResolvedProfileUserId(resolved);
          return;
        }
        // Rule 2b: slug-based public view (resolve after profile loads)
        if (publicSlugParam) {
          setResolvedFromPublic(null);
          setResolvedProfileUserId(null);
          return;
        }
        // Rule 3: fallback to viewer
        setResolvedFromPublic(null);
        setResolvedProfileUserId(viewerId || null);
      } catch {
        if (cancelled) return;
        setResolvedFromPublic(null);
        setResolvedProfileUserId(viewerId || null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uidParam, publicParam, publicSlugParam, resumeIdParam, viewerId]);

  useEffect(() => {
    if (!publicSlugParam) return;
    if (!profileOwnerId) return;
    if (resolvedProfileUserId !== profileOwnerId) {
      setResolvedProfileUserId(profileOwnerId);
    }
  }, [publicSlugParam, profileOwnerId, resolvedProfileUserId]);

  useEffect(() => {
    if (!isReadOnlyOtherProfile || !profileOwnerId) return;
    let cancelled = false;
    networkAPI.checkConnected(profileOwnerId).then((connected) => {
      if (!cancelled) setIsConnectedWithProfileUser(!!connected);
    }).catch(() => {
      if (!cancelled) setIsConnectedWithProfileUser(false);
    });
    return () => { cancelled = true; };
  }, [isReadOnlyOtherProfile, profileOwnerId]);

  // Fetch unread messaging count (for messaging button badge)
  useEffect(() => {
    if (!isAuthenticated || !canEditProfile) return;
    const fetchUnread = () => {
      messagingAPI.getUnreadCount().then((n) => setUnreadMessagingCount(n)).catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => clearInterval(t);
  }, [isAuthenticated, canEditProfile]);

  // Fetch analytics data when viewing own profile
  useEffect(() => {
    if (!showOwnerOnlyDashboard || !profile) return;
    
    const fetchAnalytics = async () => {
      setLoadingAnalytics(true);
      try {
        const AWS_API_BASE_URL = analyticsApiBaseUrl;
        if (!AWS_API_BASE_URL) {
          console.warn('[Analytics] No API base URL configured');
          return;
        }

        const token = getAnalyticsToken();
        const awsUrl = (path) => {
          const base = AWS_API_BASE_URL.replace(/\/+$/, '');
          const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
          return `${base}${p}`;
        };

        // Fetch all analytics in parallel
        // Filter by source=find_people and excludeConnected=true to match LinkedIn behavior
        const [viewsRes, searchesRes, connectionsRes, colleaguesRes] = await Promise.all([
          fetch(awsUrl('/me/analytics/profile-views?days=7&source=find_people&excludeConnected=true'), {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(awsUrl('/me/analytics/search-appearances?days=7&source=find_people&excludeConnected=true'), {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(awsUrl('/me/analytics/viewer-connections'), {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(awsUrl('/me/analytics/company-colleagues'), {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (!viewsRes.ok) {
          console.warn('[Analytics] profile-views request failed:', viewsRes.status, viewsRes.statusText);
        }
        if (!searchesRes.ok) {
          console.warn('[Analytics] search-appearances request failed:', searchesRes.status, searchesRes.statusText);
        }

        const [views, searches, connections, colleagues] = await Promise.all([
          viewsRes.ok ? viewsRes.json() : { views: [], count: 0, days: 7 },
          searchesRes.ok ? searchesRes.json() : { count: 0, topQueries: [], days: 7 },
          connectionsRes.ok ? connectionsRes.json() : { connections: [], count: 0 },
          colleaguesRes.ok ? colleaguesRes.json() : { colleagues: [], count: 0, company: '' }
        ]);

        setAnalyticsData({
          profileViews: views,
          searchAppearances: searches,
          viewerConnections: connections,
          companyColleagues: colleagues
        });
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
      } finally {
        setLoadingAnalytics(false);
      }
    };

    fetchAnalytics();
  }, [showOwnerOnlyDashboard, profile]);

  // Track profile view when someone views a profile
  useEffect(() => {
    if (!profile || !profileOwnerId) {
      addDebugLog('Tracking skipped: profile or profileOwnerId missing', 'info');
      return;
    }
    if (profileOwnerId === viewerId) {
      addDebugLog('Tracking skipped: viewing own profile', 'info');
      return;
    }
    
    addDebugLog('Initiating profile view tracking...', 'info');
    
    const trackView = async () => {
      try {
        const AWS_API_BASE_URL = analyticsApiBaseUrl;
        if (!AWS_API_BASE_URL) {
          const msg = 'No API base URL configured';
          console.warn('[Analytics]', msg);
          addDebugLog(`Error: ${msg}`, 'error');
          return;
        }

        addDebugLog(`API URL: ${AWS_API_BASE_URL}`, 'info');

        const token = getAnalyticsToken();
        const awsUrl = (path) => {
          const base = AWS_API_BASE_URL.replace(/\/+$/, '');
          const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
          return `${base}${p}`;
        };

        // Get viewer info if authenticated
        let viewerName = 'Anonymous';
        let viewerCompany = '';
        let viewerPublicLinkId = '';
        let viewerPhotoUrl = '';
        let actualViewerId = 'anonymous';

        if (viewerId && user) {
          actualViewerId = viewerId;
          viewerName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User';
          viewerPublicLinkId = user.public_link_id || '';
          addDebugLog(`Viewer: ${viewerName} (${actualViewerId})`, 'info');
          
          // Try to get company from viewer's resume
          try {
            const viewerResumeRes = await fetch(awsUrl('/me/resumes'), {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (viewerResumeRes.ok) {
              const resumeData = await viewerResumeRes.json();
              const resumes = resumeData.resumes || [];
              if (resumes.length > 0) {
                const primaryResume = resumes[0];
                const currentJob = primaryResume.experience?.find(exp => exp.is_current);
                viewerCompany = currentJob?.company || '';
                viewerPhotoUrl = primaryResume.photo_url || primaryResume.photoUrl || primaryResume.photo || '';
                if (viewerCompany) {
                  addDebugLog(`Company: ${viewerCompany}`, 'info');
                }
              }
            }
          } catch (err) {
            console.warn('[Analytics] Could not fetch viewer company:', err);
          }
        } else {
          addDebugLog('Anonymous viewer', 'info');
        }

        const trackingData = {
          profileUserId: profileOwnerId,
          viewerId: actualViewerId,
          viewerName,
          viewerCompany,
          viewerPhotoUrl,
          viewerPublicLinkId,
          source: viewSource // find_people, direct, share, etc.
        };

        console.log('[Analytics] Tracking profile view:', trackingData);
        addDebugLog(`Sending POST to /analytics/profile-view (source: ${viewSource})`, 'info');
        addDebugLog(`Payload: ${JSON.stringify(trackingData, null, 2)}`, 'info');

        // Track the view
        const response = await fetch(awsUrl('/analytics/profile-view'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify(trackingData)
        });

        if (!response.ok) {
          const msg = `Failed to track (${response.status} ${response.statusText})`;
          console.error('[Analytics]', msg);
          addDebugLog(msg, 'error');
        } else {
          const msg = 'Profile view tracked successfully!';
          console.log('[Analytics]', msg);
          addDebugLog(msg, 'success');
        }
      } catch (err) {
        const msg = `Error: ${err.message}`;
        console.error('[Analytics]', msg, err);
        addDebugLog(msg, 'error');
      }
    };

    // Add a small delay to ensure profile is fully loaded
    const timer = setTimeout(() => {
      trackView();
    }, 500);

    return () => clearTimeout(timer);
  }, [profile, profileOwnerId, viewerId, user, addDebugLog]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      let primaryResume = null;

      if (isPublicProfileView) {
        const debug = (searchParams.get('debug') || '').trim() === '1';
        const events = [];
        const log = (e) => {
          const entry = { at: Date.now(), ...e };
          events.push(entry);
          if (debug) console.log('[profile/view]', entry);
        };

        // REQUIRED precedence for viewing another user:
        // public -> cache/state -> rid (optional)

        const cacheKey = `profileViewCache:${String(publicSlugParam || publicParam || resumeIdParam || '')}`;
        const cachedCandidate = !cacheKey.endsWith(':') ? readProfileViewCache(cacheKey) : null;
        const stateCandidate = (() => {
          try {
            const st = location?.state?.profile;
            return st && typeof st === 'object' ? st : null;
          } catch {
            return null;
          }
        })();

        // 1) Public fetch (PRIMARY for cross-user viewing)
        // For AWS backend: This fetches user profile from Users table (not resumes)
        if (publicSlugParam) {
          const res = await resumeAPI.fetchPublicProfileBySlugWithMeta?.(publicSlugParam, { auth: isAuthenticated });
          log({ branch: 'public_slug_fetch', slug: publicSlugParam, ok: res?.ok, status: res?.status, count: Array.isArray(res?.list) ? res.list.length : 0, hasProfile: !!res?.profile });
          const list = Array.isArray(res?.list) ? res.list : [];
          if (list.length > 0) {
            primaryResume = list[0] || null;
          }
        } else if (publicParam) {
          const res = await resumeAPI.fetchByPublicLinkWithMeta?.(publicParam);
          log({ branch: 'public_fetch', public: publicParam, ok: res?.ok, status: res?.status, count: Array.isArray(res?.list) ? res.list.length : 0, hasProfile: !!res?.profile });
          const list = Array.isArray(res?.list) ? res.list : [];
          if (list.length > 0) {
            // Use the profile data directly (from Users table for AWS backend)
            // Don't try to pick "best resume" - use the profile as-is
            primaryResume = list[0] || null;
          }
        }

        // 2) Cache / route-state fallback (preview only)
        if (!primaryResume && cachedCandidate) {
          primaryResume = cachedCandidate;
          log({ branch: 'cache_hit', cacheKey });
        }
        if (!primaryResume && stateCandidate) {
          primaryResume = stateCandidate;
          log({ branch: 'route_state_hit' });
        }

        // 3) Rid fallback (optional)
        if (!primaryResume && resumeIdParam) {
          const res = await resumeAPI.fetchByIdViaListWithMeta?.(resumeIdParam);
          log({ branch: 'rid_via_list', rid: resumeIdParam, ok: res?.ok, status: res?.status, found: !!res?.item });
          if (res?.item) primaryResume = res.item;
        }

        // 4) UID-based fetch (for viewing other users' profiles)
        if (!primaryResume && uidParam) {
          try {
            const fetched = await resumeAPI.fetchBestResumeForUser?.(uidParam, { includeFull: true, includeNonPublic: false });
            log({ branch: 'uid_fetch', uid: uidParam, found: !!fetched });
            if (fetched) primaryResume = fetched;
          } catch (err) {
            log({ branch: 'uid_fetch_error', uid: uidParam, error: err?.message });
            console.warn('Failed to fetch profile by user ID:', err);
          }
        }

        setViewDebug(debug ? events : null);
      } else {
        // Viewing own profile - use Users table profile data (not resumes)
        if (!isAuthenticated) {
          primaryResume = null;
        } else {
          // Preferred: AWS backend - fetch from Users table via /me/profile
          const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim();
          const hasAwsBackend = !!AWS_API_BASE_URL;
          if (hasAwsBackend) {
            try {
              const awsUrl = (path) => {
                const base = AWS_API_BASE_URL.replace(/\/+$/, '');
                const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
                return `${base}${p}`;
              };
              const getAwsIdToken = () => {
                try {
                  const u = JSON.parse(localStorage.getItem('user') || '{}');
                  return u?.tokens?.idToken || u?.idToken || '';
                } catch {
                  return '';
                }
              };
              const token = getAwsIdToken();
              console.log('[ProfilePage] Fetching from Users table via /me/profile...');
              console.log('[ProfilePage] API URL:', awsUrl('/me/profile'));
              console.log('[ProfilePage] Has token:', !!token);
              const res = await fetch(awsUrl('/me/profile'), {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {})
                }
              });
              console.log('[ProfilePage] Response status:', res.status, res.statusText);
              if (res.ok) {
                const profileData = await res.json();
                console.log('[ProfilePage] Profile data from Users table:', {
                  has_profile: !!profileData?.profile,
                  has_photo_url: !!profileData?.profile?.photo_url,
                  has_banner_url: !!profileData?.profile?.banner_url,
                  photo_length: profileData?.profile?.photo_url?.length || 0,
                  banner_length: profileData?.profile?.banner_url?.length || 0
                });
                const profile = profileData?.profile || null;
                if (profile) {
                  // Helper to safely parse JSON fields
                  const safeParse = (val, defaultVal = []) => {
                    if (!val) return defaultVal;
                    if (Array.isArray(val)) return val;
                    if (typeof val === 'string') {
                      try {
                        const parsed = JSON.parse(val);
                        return Array.isArray(parsed) ? parsed : defaultVal;
                      } catch {
                        return defaultVal;
                      }
                    }
                    return defaultVal;
                  };
                  
                  // Use Users table profile data (NOT from resume)
                  // All profile fields including experience, education, etc. come from Users table
                  const profileAsResume = {
                    id: profile.user_id || profile.public_link_id,
                    _id: profile.user_id || profile.public_link_id,
                    user_id: profile.user_id,
                    owner_user_id: profile.user_id,
                    public_link_id: profile.public_link_id,
                    profile_slug: profile.profile_slug || profile.profileSlug || '',
                    title: profile.name || '',
                    headline: profile.headline || '',
                    summary: profile.about || profile.summary || '',
                    // Professional Summary from Users table (NOT from resume)
                    professional_summary: profile.professionalSummary || profile.professional_summary || '',
                    photo_url: profile.photo_url || '',
                    banner_url: profile.banner_url || '',
                    contact_email: profile.email || profile.contact?.email || '',
                    contact_phone: profile.contact?.phone || profile.phone || '',
                    location: profile.location || '',
                    linkedin_url: profile.linkedin_url || profile.linkedinUrl || profile.contact?.linkedin || '',
                    website_url: profile.website_url || profile.websiteUrl || profile.contact?.website || '',
                    credentials: profile.credentials || '',
                    pronouns: profile.pronouns || '',
                    highlight_color: profile.highlight_color || profile.highlightColor || '#3b82f6',
                    profile_frame: profile.profile_frame || profile.profileFrame || '',
                    target_role: profile.targetRole || profile.target_role || '',
                    // Rich content from Users table profile (NOT from resume)
                    experience: safeParse(profile.experience || profile.work_experience || profile.workExperience, []),
                    education: safeParse(profile.education || profile.educations, []),
                    skills: safeParse(profile.skills || profile.skill, []),
                    projects: safeParse(profile.projects || profile.project, []),
                    certifications: safeParse(profile.certifications || profile.licenses || profile.license_or_certification, []),
                    languages: safeParse(profile.languages || profile.language, []),
                    training: safeParse(profile.training || profile.professional_development, []),
                    credly_badges: safeParse(profile.credly_badges || profile.credlyBadges, []),
                    core_skills: safeParse(profile.core_skills || profile.coreSkills, []),
                    is_primary: true
                  };
                  
                  // Normalize to match ProfilePage format
                  if (resumeAPI.normalizeResumeForFullView) {
                    primaryResume = resumeAPI.normalizeResumeForFullView(profileAsResume);
                  } else {
                    primaryResume = profileAsResume;
                  }
                }
              }
            } catch (e) {
              console.error('[ProfilePage] CRITICAL: Users table fetch failed!', {
                error: e.message,
                hasToken: !!token,
                apiUrl: awsUrl('/me/profile'),
                willFallbackToResumes: true
              });
            }
          }
          
          // Fallback: Use resumes (if AWS fetch failed)
          if (!primaryResume) {
            let resumes = await resumeAPI.fetchAll({ full: true });
            // Pick the best profile and fill missing fields from other resumes (no writes).
            primaryResume =
              resumeAPI.pickBestResumeFromList?.(resumes, { includeFull: true }) ||
              (resumes || []).find(r => r.is_primary) ||
              (resumes || [])[0] ||
              null;

            // If this user's profile looks "missing" (common for protected accounts or tombstoned local ids),
            // do a SELECT-only rescue fetch that does not write/migrate anything.
            if (!primaryResume) {
              try {
                const rescued = await resumeAPI.fetchBestResumeForCurrentUser?.({ includeFull: true });
                if (rescued) primaryResume = rescued;
              } catch (e) {
                console.warn('Profile rescue fetch failed:', e);
              }
            }
          }
        }
      }

      if (primaryResume) {
        // Ensure ALL fields are explicitly preserved - don't rely on spread alone
        const profileData = {
          ...primaryResume,
          // Explicitly preserve all critical fields
          title: primaryResume.title || primaryResume.title || '',
          headline: primaryResume.headline || '',
          summary: primaryResume.summary || primaryResume.about || primaryResume.about_text || '',
          professional_summary:
            primaryResume.professional_summary ||
            primaryResume.professionalSummary ||
            '',
          photo_url: primaryResume.photo_url || primaryResume.photoUrl || primaryResume.photo || '',
          banner_url: primaryResume.banner_url || primaryResume.bannerUrl || primaryResume.banner || '',
          pronouns: primaryResume.pronouns || '',
          credentials: primaryResume.credentials || '',
          profile_slug: primaryResume.profile_slug || primaryResume.profileSlug || '',
          public_link_id: primaryResume.public_link_id || primaryResume.publicLinkId || '',
          profile_frame: primaryResume.profile_frame || '',
          target_role: primaryResume.target_role || primaryResume.targetRole || '',
          highlight_color: primaryResume.highlight_color || '#3b82f6',
          contact_email:
            primaryResume.contact_email ||
            primaryResume.contactEmail ||
            primaryResume.user_email ||
            primaryResume.userEmail ||
            primaryResume.email ||
            '',
          contact_phone: primaryResume.contact_phone || primaryResume.contactPhone || primaryResume.phone || '',
          location: primaryResume.location || primaryResume.locationText || '',
          linkedin_url: primaryResume.linkedin_url || primaryResume.linkedinUrl || primaryResume.linkedin || '',
          website_url: primaryResume.website_url || primaryResume.websiteUrl || primaryResume.website || '',
          experience:
            primaryResume.experience ||
            primaryResume.work_experience ||
            primaryResume.workExperience ||
            [],
          education: primaryResume.education || primaryResume.educations || [],
          training: primaryResume.training || primaryResume.professional_development || [],
          skills: primaryResume.skills || primaryResume.skill || [],
          projects: primaryResume.projects || primaryResume.project || [],
          certifications:
            primaryResume.certifications ||
            primaryResume.licenses ||
            primaryResume.license_or_certification ||
            [],
          languages: primaryResume.languages || primaryResume.language || []
        };
        console.log('ProfilePage - Profile data loaded:', {
          id: profileData.id || profileData._id,
          title: profileData.title,
          hasSummary: !!profileData.summary,
          summary: profileData.summary,
          hasProfessionalSummary: !!profileData.professional_summary,
          professional_summary: profileData.professional_summary,
          hasPhotoUrl: !!profileData.photo_url,
          photo_url: profileData.photo_url,
          hasBannerUrl: !!profileData.banner_url,
          banner_url: profileData.banner_url,
          hasHeadline: !!profileData.headline,
          headline: profileData.headline,
          hasTargetRole: !!profileData.target_role,
          target_role: profileData.target_role,
          experienceCount: Array.isArray(profileData.experience) ? profileData.experience.length : 0,
          experience: profileData.experience,
          educationCount: Array.isArray(profileData.education) ? profileData.education.length : 0,
          education: profileData.education
        });
        setProfile(profileData);
        setCredlyBadges(primaryResume.credly_badges || []);
        setTempPhotoUrl(primaryResume.photo_url || '');
        setTempBannerUrl(primaryResume.banner_url || '');
        setTempHighlightColor(primaryResume.highlight_color || '#3b82f6');
        setTempProfileFrame(primaryResume.profile_frame || '');
      } else {
        if (isPublicProfileView) {
          setProfile(null);
        } else {
          setProfile({
            title: user?.name || user?.email || 'My Profile',
            headline: '',
            summary: '',
            professional_summary: '',
            contact_email: user?.email || '',
            contact_phone: '',
            location: '',
            linkedin_url: '',
            website_url: '',
            photo_url: '',
            banner_url: '',
            experience: [],
            education: [],
            skills: [],
            projects: [],
            certifications: [],
            languages: []
          });
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      if (isPublicProfileView) {
        setProfile(null);
      } else {
        setProfile({
          title: user?.name || user?.email || 'My Profile',
          headline: '',
          summary: '',
          professional_summary: '',
          contact_email: user?.email || '',
          contact_phone: '',
          location: '',
          linkedin_url: '',
          website_url: '',
          photo_url: '',
          banner_url: '',
          experience: [],
          education: [],
          training: [],
          skills: [],
          projects: [],
          certifications: [],
          languages: []
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMessageUser = async (userId, draftText) => {
    if (!userId) return;
    try {
      const { conversation } = await messagingAPI.createConversation({ participant_ids: [userId] });
      const cid = conversation?.conversation_id;
      const draft = { to: userId, text: draftText || '', conversation_id: cid };
      sessionStorage.setItem('messaging:draft', JSON.stringify(draft));
      navigate(cid ? `/messaging?conversation=${encodeURIComponent(cid)}` : '/messaging');
    } catch (e) {
      console.error('Error creating conversation:', e);
      try {
        sessionStorage.setItem('messaging:draft', JSON.stringify({ to: userId, text: draftText || '' }));
      } catch { /* ignore */ }
      navigate('/messaging');
    }
  };

  const handleMessageProfile = async () => {
    const to = profileOwnerId || profile?.user_id || '';
    if (!to) {
      alert('Could not determine profile user.');
      return;
    }
    const text = 'Hi! I found your profile and wanted to connect.';
    await handleMessageUser(to, text);
  };

  const shouldGateAuthAction = (action) => {
    if (!isAuthenticated && isPublicProfileView && typeof onAuthAction === 'function') {
      onAuthAction(action);
      return true;
    }
    return false;
  };

  const handleConnectClick = () => {
    if (shouldGateAuthAction('connect')) return;
    handleConnect();
  };

  const handleMessageClick = () => {
    if (shouldGateAuthAction('message')) return;
    handleMessageProfile();
  };

  const handleMoreClick = () => {
    if (shouldGateAuthAction('more')) return;
    setMoreOpen((v) => !v);
  };

  const handleMoreAction = (action) => {
    switch (action) {
      case 'send_profile_in_message':
        handleMessageProfile();
        return;
      case 'save_to_pdf':
        handlePrintProfile();
        return;
      case 'follow':
        alert('Coming soon: Follow');
        return;
      case 'request_recommendation':
        alert('Coming soon: Request recommendation');
        return;
      case 'recommend':
        alert('Coming soon: Recommend');
        return;
      case 'unfollow':
        alert('Coming soon: Unfollow');
        return;
      case 'remove_connection':
        handleRemoveConnection();
        return;
      case 'report_block':
        alert('Coming soon: Report / Block');
        return;
      case 'about_profile':
        alert('This profile shows the member\'s professional information as they\'ve shared it on the platform.');
        return;
      default:
        alert('Coming soon.');
    }
  };

  const handleConnect = async () => {
    if (!profileOwnerId) return;
    try {
      await networkAPI.connect(profileOwnerId);
      // Connect sends a request; they are not connected until the other person accepts
      alert('Connection request sent! They will see it in My Network > Invitations and can accept to connect.');
    } catch (e) {
      console.error('Connect failed:', e);
      const msg = e?.message || 'Failed to send request';
      if (msg.includes('401') || msg.toLowerCase().includes('unauthorized') || msg.toLowerCase().includes('authentication')) {
        alert('Session expired or not signed in. Please log out and log back in, then try Connect again.');
      } else {
        alert(msg);
      }
    }
  };

  const handleRemoveConnection = async () => {
    if (!profileOwnerId) return;
    if (!window.confirm('Remove this person from your network? They will no longer appear in My Network.')) return;
    setMoreOpen(false);
    try {
      await networkAPI.disconnect(profileOwnerId);
      setIsConnectedWithProfileUser(false);
    } catch (e) {
      console.error('Remove connection failed:', e);
      alert(e?.message || 'Failed to remove connection');
    }
  };

  const handleRecoverProfile = async () => {
    // Recovery function removed per user request
    setRecoveringProfile(true);
    setRecoveryNote('Profile recovery has been disabled. Please refresh the page or contact support if you need assistance.');
    setTimeout(() => {
      setRecoveringProfile(false);
    }, 2000);
  };

  const slugifyName = (value) => {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const getProfileShareUrl = () => {
    const origin = window.location.origin;
    const existingSlug = (profile?.profile_slug || profile?.profileSlug || publicSlugParam || '').trim();
    const computedSlug = existingSlug || slugifyName(profile?.title || user?.name || user?.email || '');
    if (computedSlug) {
      return `${origin}/in/${encodeURIComponent(computedSlug)}`;
    }
    const publicId = (profile?.public_link_id || publicParam || '').trim();
    if (publicId) {
      return `${origin}/profile/view?public=${encodeURIComponent(publicId)}`;
    }
    return `${origin}/profile/view`;
  };

  useEffect(() => {
    if (!canEditProfile || !profile) return;
    const existingSlug = (profile.profile_slug || profile.profileSlug || '').trim();
    if (existingSlug) return;
    const baseName = profile.title || user?.name || user?.email || '';
    const generated = slugifyName(baseName);
    if (!generated) return;
    let cancelled = false;
    const updateSlug = async () => {
      try {
        const updated = await resumeAPI.updateMyProfile({ profile_slug: generated });
        if (cancelled) return;
        setProfile((prev) => ({
          ...prev,
          profile_slug: updated?.profile_slug || generated
        }));
      } catch (error) {
        console.warn('Failed to backfill profile slug:', error);
        if (!cancelled) {
          setProfile((prev) => ({
            ...prev,
            profile_slug: generated
          }));
        }
      }
    };
    updateSlug();
    return () => { cancelled = true; };
  }, [canEditProfile, profile?.profile_slug, profile?.profileSlug, profile?.title, user?.name, user?.email]);

  useEffect(() => {
    const handleAfterPrint = () => setIsPrinting(false);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, []);

  const handlePrintProfile = () => {
    setIsPrinting(true);
    requestAnimationFrame(() => {
      window.print();
    });
  };

  const handleSaveToUsersTable = async () => {
    if (!profile) {
      alert('Profile not loaded');
      return;
    }
    
    if (!isAuthenticated) {
      alert('Please log in to save your profile to the Users table.');
      return;
    }
    
    setSavingToUsersTable(true);
    try {
      await resumeAPI.saveProfileToUsersTable(profile);
      alert('Profile saved to Users table successfully! This will update the Find People view.');
    } catch (error) {
      const errorMsg = error?.message || 'Unknown error';
      if (errorMsg.includes('Authentication') || errorMsg.includes('log in') || errorMsg.includes('token')) {
        alert(`Authentication error: ${errorMsg}\n\nPlease log out and log back in to refresh your session, then try again.`);
      } else {
        alert(`Failed to save to Users table: ${errorMsg}`);
      }
      console.error('Save to Users table error:', error);
    } finally {
      setSavingToUsersTable(false);
    }
  };

  const handleStartEditingImages = () => {
    if (!profile) {
      console.error('Cannot edit images: profile is not loaded');
      alert('Profile not loaded. Please refresh the page.');
      return;
    }
    setTempPhotoUrl(profile.photo_url || '');
    setTempBannerUrl(profile.banner_url || '');
    setTempHighlightColor(profile.highlight_color || '#3b82f6');
    const frame = profile.profile_frame || '';
    setTempProfileFrame(frame);
    // Initialize custom frame text if it exists
    if (frame.startsWith('custom:')) {
      setCustomFrameText(frame.substring(7));
    } else {
      setCustomFrameText('');
    }
    setEditingImages(true);
  };

  const handleCancelEditingImages = () => {
    setEditingImages(false);
    if (profile) {
      setTempPhotoUrl(profile.photo_url || '');
      setTempBannerUrl(profile.banner_url || '');
      setTempHighlightColor(profile.highlight_color || '#3b82f6');
      const frame = profile.profile_frame || '';
      setTempProfileFrame(frame);
      if (frame.startsWith('custom:')) {
        setCustomFrameText(frame.substring(7));
      } else {
        setCustomFrameText('');
      }
    }
  };

  const handleSaveImages = async () => {
    setSavingImages(true);
    try {
      if (!(profile.id || profile._id)) {
        alert('Profile ID not found. Please refresh the page and try again.');
        setSavingImages(false);
        return;
      }

      // Get AWS API configuration (with fallback like api.js)
      const AWS_API_BASE_URL_FALLBACK = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
      const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim() || AWS_API_BASE_URL_FALLBACK;

      const getAwsIdToken = () => {
        try {
          const u = JSON.parse(localStorage.getItem('user') || '{}');
          return u?.tokens?.idToken || u?.idToken || '';
        } catch { return ''; }
      };

      const token = getAwsIdToken();
      if (!token) {
        alert('Not authenticated. Please log in again.');
        setSavingImages(false);
        return;
      }

      const awsUrl = (path) => {
        const base = AWS_API_BASE_URL.replace(/\/+$/, '');
        const p = String(path || '').startsWith('/') ? path : `/${path}`;
        return `${base}${p}`;
      };

      // Determine final photo URL
      let photoUrl;
      if (tempPhotoUrl === '') {
        // User explicitly deleted the photo
        photoUrl = '';
        console.log('[ProfilePage] Profile picture deleted');
      } else if (tempPhotoUrl instanceof Blob) {
        // User uploaded a new file - need to upload to S3
        console.log('[ProfilePage] Uploading profile picture to S3...');
        
        try {
          // 1. Get presigned URL for avatar
          const presignRes = await fetch(awsUrl('/uploads/presign'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              imageType: 'avatar',
              contentType: 'image/jpeg'
            })
          });

          if (!presignRes.ok) {
            throw new Error('Failed to get upload URL for profile picture');
          }

          const { putUrl, publicUrl } = await presignRes.json();

          // 2. Upload to S3
          const uploadRes = await fetch(putUrl, {
            method: 'PUT',
            headers: { 
              'Content-Type': 'image/jpeg'
            },
            body: tempPhotoUrl
          });

          if (!uploadRes.ok) {
            throw new Error(`Failed to upload profile picture to S3: ${uploadRes.status}`);
          }

          // Save clean URL (without query params) to database
          photoUrl = publicUrl;
          console.log('[ProfilePage] Profile picture uploaded to:', publicUrl);
        } catch (uploadErr) {
          console.error('[ProfilePage] Profile picture upload failed:', uploadErr);
          alert(`Failed to upload profile picture: ${uploadErr.message}`);
          setSavingImages(false);
          return;
        }
      } else if (typeof tempPhotoUrl === 'string' && tempPhotoUrl.startsWith('data:')) {
        // Data URL needs conversion and upload
        console.log('[ProfilePage] Converting and uploading data URL to S3...');
        
        try {
          const presignRes = await fetch(awsUrl('/uploads/presign'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              imageType: 'avatar',
              contentType: 'image/jpeg'
            })
          });

          if (!presignRes.ok) {
            throw new Error('Failed to get upload URL for profile picture');
          }

          const { putUrl, publicUrl } = await presignRes.json();

          // Convert data URL to blob
          const photoBlob = await (await fetch(tempPhotoUrl)).blob();

          // Upload to S3
          const uploadRes = await fetch(putUrl, {
            method: 'PUT',
            headers: { 
              'Content-Type': 'image/jpeg'
            },
            body: photoBlob
          });

          if (!uploadRes.ok) {
            throw new Error(`Failed to upload profile picture to S3: ${uploadRes.status}`);
          }

          photoUrl = publicUrl;
          console.log('[ProfilePage] Profile picture uploaded to:', publicUrl);
        } catch (uploadErr) {
          console.error('[ProfilePage] Profile picture upload failed:', uploadErr);
          alert(`Failed to upload profile picture: ${uploadErr.message}`);
          setSavingImages(false);
          return;
        }
      } else if (typeof tempPhotoUrl === 'string' && tempPhotoUrl.startsWith('http')) {
        // Already a URL, use as-is
        photoUrl = tempPhotoUrl;
      } else {
        // No change, keep existing URL
        photoUrl = profile.photo_url || '';
      }

      // Determine final banner URL
      let bannerUrl;
      if (tempBannerUrl === '') {
        // User explicitly deleted the banner
        bannerUrl = '';
        console.log('[ProfilePage] Banner deleted');
      } else if (tempBannerUrl instanceof Blob) {
        // User uploaded a new file - need to upload to S3
        console.log('[ProfilePage] Uploading banner to S3...');
        
        try {
          // 1. Get presigned URL for banner
          const presignRes = await fetch(awsUrl('/uploads/presign'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              imageType: 'banner',
              contentType: 'image/jpeg'
            })
          });

          if (!presignRes.ok) {
            throw new Error('Failed to get upload URL for banner');
          }

          const { putUrl, publicUrl } = await presignRes.json();

          // 2. Upload to S3
          const uploadRes = await fetch(putUrl, {
            method: 'PUT',
            headers: { 
              'Content-Type': 'image/jpeg'
            },
            body: tempBannerUrl
          });

          if (!uploadRes.ok) {
            throw new Error(`Failed to upload banner to S3: ${uploadRes.status}`);
          }

          // Save clean URL (without query params) to database
          bannerUrl = publicUrl;
          console.log('[ProfilePage] Banner uploaded to:', publicUrl);
        } catch (uploadErr) {
          console.error('[ProfilePage] Banner upload failed:', uploadErr);
          alert(`Failed to upload banner: ${uploadErr.message}`);
          setSavingImages(false);
          return;
        }
      } else if (typeof tempBannerUrl === 'string' && tempBannerUrl.startsWith('data:')) {
        // Data URL needs conversion and upload
        console.log('[ProfilePage] Converting and uploading data URL to S3...');
        
        try {
          const presignRes = await fetch(awsUrl('/uploads/presign'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              imageType: 'banner',
              contentType: 'image/jpeg'
            })
          });

          if (!presignRes.ok) {
            throw new Error('Failed to get upload URL for banner');
          }

          const { putUrl, publicUrl } = await presignRes.json();

          // Convert data URL to blob
          const bannerBlob = await (await fetch(tempBannerUrl)).blob();

          // Upload to S3
          const uploadRes = await fetch(putUrl, {
            method: 'PUT',
            headers: { 
              'Content-Type': 'image/jpeg'
            },
            body: bannerBlob
          });

          if (!uploadRes.ok) {
            throw new Error(`Failed to upload banner to S3: ${uploadRes.status}`);
          }

          bannerUrl = publicUrl;
          console.log('[ProfilePage] Banner uploaded to:', publicUrl);
        } catch (uploadErr) {
          console.error('[ProfilePage] Banner upload failed:', uploadErr);
          alert(`Failed to upload banner: ${uploadErr.message}`);
          setSavingImages(false);
          return;
        }
      } else if (typeof tempBannerUrl === 'string' && tempBannerUrl.startsWith('http')) {
        // Already a URL, use as-is
        bannerUrl = tempBannerUrl;
      } else {
        // No change, keep existing URL
        bannerUrl = profile.banner_url || '';
      }

      // 4. Save CloudFront URLs (NOT base64) to database
      const profileData = {
        ...profile,
        photo_url: photoUrl,
        banner_url: bannerUrl,
        highlight_color: tempHighlightColor,
        profile_frame: tempProfileFrame,
        is_primary: true,
        public_link_id: profile.public_link_id || generatePublicLinkId(),
        credly_badges: credlyBadges
      };

      console.log('[ProfilePage] Saving CloudFront URLs to Users table...', {
        photo_url: photoUrl,
        banner_url: bannerUrl
      });

      // Save to Users table
      try {
        await resumeAPI.saveProfileToUsersTable(profileData);
        console.log('[ProfilePage] Successfully saved URLs to Users table');
      } catch (profileErr) {
        console.error('[ProfilePage] Save to Users table failed:', profileErr);
        const msg = profileErr?.message || profileErr?.error || 'Could not save to profile';
        alert(`Profile save failed: ${msg}`);
        setSavingImages(false);
        return;
      }

      // Sync to Resumes table
      try {
        await resumeAPI.update(profile.id || profile._id, profileData);
        console.log('[ProfilePage] Successfully synced to Resumes table');
      } catch (resumeErr) {
        console.warn('[ProfilePage] Resume table sync failed:', resumeErr);
      }

      // Update UI immediately with new URLs
      setProfile(profileData);
      setTempPhotoUrl(photoUrl);
      setTempBannerUrl(bannerUrl);
      setEditingImages(false);
      alert('Profile picture and banner updated successfully!');
      setSavingImages(false);

      // Notify Layout component to refresh navbar profile picture
      window.dispatchEvent(new CustomEvent('profilePictureUpdated', { 
        detail: { photoUrl } 
      }));

    } catch (error) {
      console.error('[ProfilePage] Save failed:', error);
      alert(`Failed to save changes: ${error.message}`);
      setSavingImages(false);
    }
  };

  const handleImageFileSelect = (type, file) => {
    if (file && file.type.startsWith('image/')) {
      // File objects extend Blob, so we can use them directly
      // Store the file directly for efficient S3 upload
      if (type === 'photo') {
        setTempPhotoUrl(file);
        console.log('[ProfilePage] New profile photo selected, ready for upload');
      } else {
        setTempBannerUrl(file);
        console.log('[ProfilePage] New banner selected, ready for upload');
      }
    }
  };

  const handleOrganizationLogoUpload = async (expIndex, file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    setUploadingLogo({ expIndex });
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result;
        
        // Update the experience in the profile
        const updatedExperiences = [...(profile.experience || [])];
        updatedExperiences[expIndex] = {
          ...updatedExperiences[expIndex],
          organization_logo: dataUrl
        };

        const updatedProfile = {
          ...profile,
          experience: updatedExperiences
        };

        // Save to API
        const res = await resumeAPI.update(profile.id || profile._id, updatedProfile);
        setProfile(updatedProfile);
        setUploadingLogo({ expIndex: null });
        notifySaveResult(res, null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading organization logo:', error);
      alert('Failed to upload logo. Please try again.');
      setUploadingLogo({ expIndex: null });
    }
  };

  const handleEducationLogoUpload = async (eduIndex, file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    setUploadingEduLogo({ eduIndex });
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result;
        
        // Update the education in the profile
        const updatedEducation = [...(profile.education || [])];
        updatedEducation[eduIndex] = {
          ...updatedEducation[eduIndex],
          institution_logo: dataUrl
        };

        const updatedProfile = {
          ...profile,
          education: updatedEducation
        };

        // Save to API
        const res = await resumeAPI.update(profile.id || profile._id, updatedProfile);
        setProfile(updatedProfile);
        setUploadingEduLogo({ eduIndex: null });
        notifySaveResult(res, null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading education logo:', error);
      alert('Failed to upload logo. Please try again.');
      setUploadingEduLogo({ eduIndex: null });
    }
  };

  const handleCertificationLogoUpload = async (certIndex, file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    setUploadingCertLogo({ certIndex });
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result;
        
        // Update the certification in the profile
        const updatedCertifications = [...(profile.certifications || [])];
        updatedCertifications[certIndex] = {
          ...updatedCertifications[certIndex],
          certification_logo: dataUrl
        };

        const updatedProfile = {
          ...profile,
          certifications: updatedCertifications
        };

        // Save to API
        const res = await resumeAPI.update(profile.id || profile._id, updatedProfile);
        setProfile(updatedProfile);
        setUploadingCertLogo({ certIndex: null });
        notifySaveResult(res, null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading certification logo:', error);
      alert('Failed to upload logo. Please try again.');
      setUploadingCertLogo({ certIndex: null });
    }
  };

  const handleEditExperience = (expIndex) => {
    const exp = profile.experience[expIndex];
    if (exp) {
      setEditingExpData({ ...exp }); // Create a copy for editing
      setEditingExperience(expIndex);
    }
  };

  const handleCancelEditExperience = () => {
    setEditingExperience(null);
    setEditingExpData(null);
  };

  const handleSaveExperience = async () => {
    if (editingExperience === null || !editingExpData) return;

    setSavingExperience(true);
    try {
      const updatedExperiences = [...(profile.experience || [])];
      updatedExperiences[editingExperience] = editingExpData;

      const updatedProfile = {
        ...profile,
        experience: updatedExperiences
      };

      // Save to API
      const res = await resumeAPI.update(profile.id || profile._id, updatedProfile);
      setProfile(updatedProfile);
      setEditingExperience(null);
      setEditingExpData(null);
      notifySaveResult(res, null);
      
      // Notify Resume Builder that profile was updated
      localStorage.setItem('profile_last_updated', Date.now().toString());
    } catch (error) {
      console.error('Error saving experience:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingExperience(false);
    }
  };

  const updateEditingExpField = (field, value) => {
    setEditingExpData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditAbout = () => {
    setEditingAboutText(profile.summary || '');
    setEditingAbout(true);
  };

  const handleCancelEditAbout = () => {
    setEditingAbout(false);
    setEditingAboutText('');
  };

  const handleSaveAbout = async () => {
    setSavingAbout(true);
    try {
      const updatedProfile = {
        ...profile,
        summary: editingAboutText
      };
      const res = await resumeAPI.update(profile.id || profile._id, updatedProfile);
      setProfile(updatedProfile);
      setEditingAbout(false);
      setEditingAboutText('');
      notifySaveResult(res, null);
      
      // Notify Resume Builder that profile was updated
      localStorage.setItem('profile_last_updated', Date.now().toString());
    } catch (error) {
      console.error('Error saving about:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingAbout(false);
    }
  };

  const handleEditProfessionalSummary = () => {
    setEditingProfessionalSummaryText(profile.professional_summary || '');
    setEditingProfessionalSummary(true);
  };

  const handleCancelEditProfessionalSummary = () => {
    setEditingProfessionalSummary(false);
    setEditingProfessionalSummaryText('');
  };

  const handleSaveProfessionalSummary = async () => {
    setSavingProfessionalSummary(true);
    try {
      const updatedProfile = {
        ...profile,
        professional_summary: editingProfessionalSummaryText
      };
      const res = await resumeAPI.update(profile.id || profile._id, updatedProfile);
      setProfile(updatedProfile);
      setEditingProfessionalSummary(false);
      setEditingProfessionalSummaryText('');
      notifySaveResult(res, null);
      
      // Notify Resume Builder that profile was updated
      localStorage.setItem('profile_last_updated', Date.now().toString());
    } catch (error) {
      console.error('Error saving professional summary:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingProfessionalSummary(false);
    }
  };

  const handleEditEducation = (index) => {
    const edu = profile.education[index];
    setEditingEduData({ ...edu });
    setEditingEducation(index);
  };

  const handleCancelEditEducation = () => {
    setEditingEducation(null);
    setEditingEduData(null);
  };

  const handleSaveEducation = async () => {
    setSavingEducation(true);
    try {
      const updatedEducation = [...(profile.education || [])];
      updatedEducation[editingEducation] = editingEduData;

      const updatedProfile = {
        ...profile,
        education: updatedEducation
      };

      const res = await resumeAPI.update(profile.id || profile._id, updatedProfile);
      setProfile(updatedProfile);
      setEditingEducation(null);
      setEditingEduData(null);
      notifySaveResult(res, null);
    } catch (error) {
      console.error('Error saving education:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingEducation(false);
    }
  };

  const updateEditingEduField = (field, value) => {
    setEditingEduData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditCertification = (index) => {
    const cert = profile.certifications[index];
    setEditingCertData({ ...cert });
    setEditingCertification(index);
  };

  const handleCancelEditCertification = () => {
    setEditingCertification(null);
    setEditingCertData(null);
  };

  const handleSaveCertification = async () => {
    setSavingCertification(true);
    try {
      const updatedCertifications = [...(profile.certifications || [])];
      updatedCertifications[editingCertification] = editingCertData;

      const updatedProfile = {
        ...profile,
        certifications: updatedCertifications
      };

      const res = await resumeAPI.update(profile.id || profile._id, updatedProfile);
      setProfile(updatedProfile);
      setEditingCertification(null);
      setEditingCertData(null);
      notifySaveResult(res, null);
    } catch (error) {
      console.error('Error saving certification:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingCertification(false);
    }
  };

  const updateEditingCertField = (field, value) => {
    setEditingCertData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearchPeople = async (e) => {
    e?.preventDefault();
    const q = (searchQuery || '').trim();
    if (!q) {
      setSearchResults([]);
      setSearchError('');
      return;
    }
    setSearching(true);
    setSearchError('');
    try {
      const results = await resumeAPI.searchPublicProfiles(q, 25, { includeNonPublic: false, includeFull: false });
      setSearchResults(results || []);
      
      // Emit search_appearance events for each user in results
      if (results && results.length > 0) {
        trackSearchAppearances(results, q);
      }
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
      setSearchError(err?.message || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };
  
  // Track search appearances when users appear in Find People results
  const trackSearchAppearances = async (results, searchQuery) => {
    try {
      const AWS_API_BASE_URL = analyticsApiBaseUrl;
      if (!AWS_API_BASE_URL) return;

      const token = getAnalyticsToken();
      const awsUrl = (path) => {
        const base = AWS_API_BASE_URL.replace(/\/+$/, '');
        const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
        return `${base}${p}`;
      };
      
      // Track each user that appeared in search results
      const trackingPromises = results.map(async (result) => {
        const userId = result.profile_user_id;
        if (!userId) return;
        
        try {
          await fetch(awsUrl('/analytics/search-appearance'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              userId,
              searchQuery,
              searchingUserId: viewerId || 'anonymous',
              source: 'find_people'
            })
          });
          
          console.log(`[Analytics] Search appearance tracked for user ${userId}`);
          addDebugLog(`Search appearance: ${result.title || userId} in "${searchQuery}"`, 'success');
        } catch (err) {
          console.warn(`[Analytics] Failed to track search appearance for ${userId}:`, err);
        }
      });
      
      // Fire all tracking requests in parallel (don't wait for them)
      Promise.all(trackingPromises).catch(err => {
        console.error('[Analytics] Search appearance tracking failed:', err);
      });
    } catch (err) {
      console.error('[Analytics] Error setting up search appearance tracking:', err);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // If no profile and not authenticated, redirect to login or show message
  if (!profile && !isAuthenticated && !isPublicProfileView) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container-custom">
          <div className="card mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome</h1>
            <p className="text-sm text-gray-600 mb-4">
              Please log in to view your profile or create a new one.
            </p>
            <Link to="/login" className="btn btn-primary">
              Log In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    if (isPublicProfileView) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center px-6">
            <p className="text-gray-700 mb-2 font-medium">Profile not found.</p>
            <p className="text-sm text-gray-500 mb-5">
              This usually means the user hasn’t published their profile yet (no public link), or the link is invalid.
            </p>
            <Link to="/profile" className="btn btn-primary">
              Back to Profile
            </Link>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            No profile found
          </p>
          <Link to="/profile/edit" className="btn btn-primary">
            Create Your Profile
          </Link>
        </div>
      </div>
    );
  }

  // Get university information for top display
  const getUniversityInfo = () => {
    if (!profile.education || profile.education.length === 0) return null;
    
    // Find education entries that are completed (have end_date) or currently attending (end_date is 'Present' or empty)
    const universityEntries = profile.education.filter(edu => 
      edu.institution && (edu.end_date || edu.end_date === 'Present' || !edu.end_date)
    );
    
    if (universityEntries.length === 0) return null;
    
    // Return the most recent or first one
    return universityEntries[0];
  };

  const universityInfo = getUniversityInfo();

  // Format name with credentials
  const formatNameWithCredentials = () => {
    let name = profile.title || '';
    if (profile.credentials) {
      name += ` - ${profile.credentials}`;
    }
    return name;
  };

  // Group consecutive same-org experiences for display (org header once, roles below)
  const experienceGroups = (() => {
    const list = profile.experience || [];
    if (!list.length) return [];
    const groups = [];
    let current = { company: list[0].company, organization_logo: list[0].organization_logo, employment_type: list[0].employment_type, location_type: list[0].location_type, experiences: [{ exp: list[0], index: 0 }] };
    for (let i = 1; i < list.length; i++) {
      const exp = list[i];
      const same = current.company && exp.company && current.company.toLowerCase().trim() === exp.company.toLowerCase().trim();
      if (same) {
        current.experiences.push({ exp, index: i });
        if (!current.employment_type && exp.employment_type) current.employment_type = exp.employment_type;
        if (!current.location_type && exp.location_type) current.location_type = exp.location_type;
      } else {
        groups.push(current);
        current = { company: exp.company, organization_logo: exp.organization_logo, employment_type: exp.employment_type, location_type: exp.location_type, experiences: [{ exp, index: i }] };
      }
    }
    groups.push(current);
    return groups;
  })();

  return (
    <div id="print-profile" className="min-h-screen bg-gray-50">
      {/* Banner Section with Overlapping Profile Picture */}
      <div
        className={`h-64 bg-gradient-to-r from-primary-600 to-primary-800 relative w-full ${canEditProfile ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
        onClick={canEditProfile ? handleStartEditingImages : undefined}
        title={canEditProfile ? 'Click to edit banner and profile picture' : ''}
        style={
          profile.banner_url
            ? {
                backgroundImage: `linear-gradient(rgba(37, 99, 235, 0.3), rgba(29, 78, 216, 0.3)), url(${cachedBannerUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                width: '100%'
              }
            : {
                width: '100%'
              }
        }
      >
        <div className="container-custom h-full flex items-end pb-8 relative mx-auto">
          {/* Profile Picture Overlapping Banner - Always Show */}
          <div 
            className={`absolute -bottom-12 left-8 z-20 ${canEditProfile ? 'cursor-pointer' : ''}`}
            onClick={canEditProfile ? (e) => { e.stopPropagation(); handleStartEditingImages(); } : undefined}
            title={canEditProfile ? 'Click to edit profile picture and banner' : ''}
          >
            <div className="relative">
              {/* Background border circle */}
              <div 
                className="absolute inset-0 rounded-full p-1.5 shadow-2xl"
                style={{ 
                  backgroundColor: profile.highlight_color || '#3b82f6',
                  padding: '6px'
                }}
              >
                <div className="w-full h-full rounded-full bg-white"></div>
              </div>
              {/* Profile picture with frame */}
              <ProfilePictureFrame 
                photoUrl={cachedPhotoUrl}
                frameOption={profile.profile_frame}
                size="w-48 h-48"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Profile Information Section - Below Picture Area */}
      <div className="container-custom pt-20 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-start">
          {/* Left column: Name and Headline share same left edge */}
          <div className="min-w-0">
            {/* Name with Credentials */}
            <div className="flex items-start gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{formatNameWithCredentials()}</h1>
              {forceReadOnly && (
                <span className="mt-1 inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 border border-gray-200">
                  View profile (read-only)
                </span>
              )}
              {Array.isArray(viewDebug) && viewDebug.length > 0 && (
                <span className="mt-1 inline-flex items-center rounded-full bg-yellow-50 px-3 py-1 text-xs font-semibold text-yellow-800 border border-yellow-200">
                  debug: {String(viewDebug[viewDebug.length - 1]?.branch || 'n/a')}
                  {typeof viewDebug[viewDebug.length - 1]?.status === 'number'
                    ? ` (${viewDebug[viewDebug.length - 1].status})`
                    : ''}
                </span>
              )}
            </div>
            
            {/* Pronouns */}
            {profile.pronouns && (
              <p className="text-sm text-gray-600 mb-2">{profile.pronouns}</p>
            )}
            
            {/* University at top if degree earned or currently attending */}
            {universityInfo && (
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                {/* Institution logo or upload icon where cap was */}
                {(() => {
                  const currentEduIndex = profile.education.findIndex(edu => 
                    edu.institution === universityInfo.institution && 
                    (!edu.end_date || edu.end_date === 'Present' || edu.is_expected || !edu.end_date)
                  );
                  const currentEdu = currentEduIndex >= 0 ? profile.education[currentEduIndex] : null;
                  return currentEdu ? (
                    <>
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        id={`currently-attending-logo-${currentEduIndex}`} 
                        disabled={uploadingEduLogo.eduIndex === currentEduIndex}
                        onChange={(e) => { 
                          const f = e.target.files?.[0]; 
                          if (f) handleEducationLogoUpload(currentEduIndex, f); 
                          e.target.value = ''; 
                        }} 
                      />
                      <label 
                        htmlFor={`currently-attending-logo-${currentEduIndex}`} 
                        className="cursor-pointer inline-flex items-center transition-colors flex-shrink-0"
                        title={currentEdu.institution_logo ? "Click to replace logo" : "Upload institution logo"}
                      >
                        {uploadingEduLogo.eduIndex === currentEduIndex ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                        ) : currentEdu.institution_logo ? (
                          <img 
                            src={currentEdu.institution_logo} 
                            alt={`${currentEdu.institution} logo`} 
                            className="w-8 h-8 object-contain rounded"
                          />
                        ) : (
                          <Image className="w-6 h-6 text-primary-600 hover:text-primary-800" />
                        )}
                      </label>
                    </>
                  ) : (
                    <GraduationCap className="w-5 h-5 flex-shrink-0" />
                  );
                })()}
                <span className="text-base">
                  {universityInfo.institution}
                  {universityInfo.degree && ` • ${universityInfo.degree}`}
                  {universityInfo.end_date && universityInfo.end_date !== 'Present' && ` • ${universityInfo.end_date}`}
                  {(!universityInfo.end_date || universityInfo.end_date === 'Present') && ' • Currently Attending'}
                </span>
              </div>
            )}
            
            {/* Target Role */}
            {profile.target_role && (
              <p className="text-base text-gray-600 mb-4">{profile.target_role}</p>
            )}
            
            {/* Professional Headline - same left column as name, first letters align */}
            {profile.headline && (
              <p className="text-lg text-gray-700 leading-relaxed max-w-xl mt-4" style={{ lineHeight: '1.6' }}>
                {profile.headline}
              </p>
            )}

            {/* Connect, Message, More - below headline when viewing another member's profile */}
            {showPublicActions && (
              <div className="flex flex-wrap items-center gap-3 mt-4">
                {!isConnectedWithProfileUser && (
                  <button
                    type="button"
                    onClick={handleConnectClick}
                    className="bg-primary-600 hover:bg-primary-700 text-white rounded-full px-4 py-2 inline-flex items-center gap-2 font-medium text-sm transition-colors"
                    title="Connect"
                  >
                    <UserPlus className="w-5 h-5" />
                    <span>Connect</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleMessageClick}
                  className="bg-primary-600 hover:bg-primary-700 text-white rounded-full px-4 py-2 inline-flex items-center gap-2 font-medium text-sm transition-colors"
                >
                  <Send className="w-5 h-5" />
                  <span>Message</span>
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={handleMoreClick}
                    className="border-2 border-gray-300 hover:border-gray-400 bg-white text-gray-700 rounded-full px-4 py-2 inline-flex items-center gap-2 font-medium text-sm transition-colors"
                  >
                    <MoreHorizontal className="w-5 h-5" />
                    <span>More</span>
                  </button>
                  {moreOpen && (
                    <div className="absolute left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                      {!isConnectedWithProfileUser ? (
                        <>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('send_profile_in_message'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm flex items-center gap-3"
                          >
                            <Share2 className="w-4 h-4 text-gray-500" />
                            Send profile in a message
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('save_to_pdf'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm flex items-center gap-3"
                          >
                            <Download className="w-4 h-4 text-gray-500" />
                            Save to PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('follow'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm flex items-center gap-3"
                          >
                            <Plus className="w-4 h-4 text-gray-500" />
                            + Follow
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('report_block'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm flex items-center gap-3"
                          >
                            <Flag className="w-4 h-4 text-gray-500" />
                            Report / Block
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('about_profile'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm flex items-center gap-3"
                          >
                            <Info className="w-4 h-4 text-gray-500" />
                            About this profile
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('send_profile_in_message'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm flex items-center gap-3"
                          >
                            <Share2 className="w-4 h-4 text-gray-500" />
                            Send profile in a message
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('save_to_pdf'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm flex items-center gap-3"
                          >
                            <Download className="w-4 h-4 text-gray-500" />
                            Save to PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('request_recommendation'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm"
                          >
                            Request recommendation
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('recommend'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm"
                          >
                            Recommend
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('unfollow'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm"
                          >
                            Unfollow
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('remove_connection'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm text-red-600 font-medium"
                          >
                            Remove connection
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('report_block'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm flex items-center gap-3"
                          >
                            <Flag className="w-4 h-4 text-gray-500" />
                            Report / Block
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMoreOpen(false); handleMoreAction('about_profile'); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm flex items-center gap-3"
                          >
                            <Info className="w-4 h-4 text-gray-500" />
                            About this profile
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right column: Edit buttons, then Contact */}
          <div className="flex flex-col gap-6 items-end lg:items-end">
            {/* Action Buttons - Below Banner (own profile only) */}
            {canEditProfile && (
            <div className="flex flex-wrap gap-3 justify-end no-print" data-html2canvas-ignore>
              <button
                onClick={handleStartEditingImages}
                className="btn btn-primary inline-flex items-center space-x-2"
                title="Edit Profile Picture & Banner"
              >
                <Camera className="w-5 h-5" />
                <span>Edit Pictures</span>
              </button>
              <Link
                to="/profile/edit"
                className="btn btn-primary inline-flex items-center space-x-2"
              >
                <Edit className="w-5 h-5" />
                <span>Edit Profile</span>
              </Link>
              <button
                onClick={handlePrintProfile}
                className="btn btn-outline inline-flex items-center space-x-2"
                disabled={isPrinting}
              >
                <Download className="w-5 h-5" />
                <span>{isPrinting ? 'Opening Print...' : 'Download'}</span>
              </button>
              <button
                onClick={() => {
                  const url = getProfileShareUrl();
                  navigator.clipboard.writeText(url);
                  alert('Profile link copied to clipboard!');
                }}
                className="btn btn-outline inline-flex items-center space-x-2"
              >
                <Share2 className="w-5 h-5" />
                <span>Share</span>
              </button>
              <Link
                to="/messaging"
                className="btn btn-outline inline-flex items-center space-x-2 relative"
              >
                <MessageSquare className="w-5 h-5" />
                <span>Messaging</span>
                {unreadMessagingCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary-600 text-white text-xs font-medium">
                    {unreadMessagingCount > 99 ? '99+' : unreadMessagingCount}
                  </span>
                )}
              </Link>
            </div>
            )}

            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-md p-6 w-full lg:w-auto lg:min-w-[280px]">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-3">
                  {profile.contact_email && (
                    <a
                      href={`mailto:${profile.contact_email}`}
                      className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                    >
                      <Mail className="w-5 h-5" />
                      <span className="text-sm">{profile.contact_email}</span>
                    </a>
                  )}
                  {profile.contact_phone && (
                    <a
                      href={`tel:${profile.contact_phone}`}
                      className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                    >
                      <Phone className="w-5 h-5" />
                      <span className="text-sm">{profile.contact_phone}</span>
                    </a>
                  )}
                  {profile.location && (
                    <div className="flex items-center gap-3 text-gray-600">
                      <MapPin className="w-5 h-5" />
                      <span className="text-sm">{profile.location}</span>
                    </div>
                  )}
                  {profile.linkedin_url && (
                    <a
                      href={profile.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                      <span className="text-sm">LinkedIn Profile</span>
                    </a>
                  )}
                  {profile.website_url && (
                    <a
                      href={profile.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                    >
                      <Globe className="w-5 h-5" />
                      <span className="text-sm">Personal Website</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
        </div>
      </div>

      {/* Edit Pictures Modal */}
      {canEditProfile && editingImages && (
        <div 
          className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
          onClick={handleCancelEditingImages}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative z-[101]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Camera className="w-6 h-6 text-primary-600" />
                  Edit Profile Picture & Banner
                </h2>
                <button
                  onClick={handleCancelEditingImages}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Profile Picture Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile Picture
                  </label>
                  <div className="space-y-4">
                    {/* Preview */}
                    <div className="relative inline-block">
                      <div 
                        className="absolute inset-0 rounded-full p-1.5 shadow-lg"
                        style={{ 
                          backgroundColor: tempHighlightColor,
                          padding: '6px'
                        }}
                      >
                        <div className="w-full h-full rounded-full bg-white"></div>
                      </div>
                      <ProfilePictureFrame 
                        photoUrl={getPreviewUrl(tempPhotoUrl)}
                        frameOption={tempProfileFrame}
                        size="w-32 h-32"
                      />
                    </div>
                    
                    {/* File Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Upload Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleImageFileSelect('photo', file);
                          }
                          // Reset the input to allow re-uploading the same file or selecting a new file
                          e.target.value = '';
                        }}
                        className="input"
                      />
                    </div>

                    {/* URL Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Or Enter Image URL
                      </label>
                      <input
                        type="url"
                        value={typeof tempPhotoUrl === 'string' ? tempPhotoUrl : ''}
                        onChange={(e) => setTempPhotoUrl(e.target.value)}
                        className="input"
                        placeholder="https://example.com/your-photo.jpg"
                      />
                      {tempPhotoUrl instanceof Blob && (
                        <p className="text-xs text-gray-500 mt-1">File selected: {tempPhotoUrl.name || 'uploaded-image.jpg'}</p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {tempPhotoUrl && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingImageType('photo');
                              setShowImageEditor(true);
                            }}
                            className="flex-1 btn btn-outline text-sm inline-flex items-center justify-center gap-2"
                          >
                            <Edit className="w-4 h-4" />
                            Edit/Crop Photo
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm('Delete profile picture?')) {
                                setTempPhotoUrl('');
                              }
                            }}
                            className="btn btn-secondary text-sm p-2 hover:bg-red-100 hover:text-red-600"
                            title="Delete photo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                    
                    {/* Profile Frame Options */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profile Picture Frame
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {/* Job Seeker Options (Bright Green with Shadow) */}
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-600 mb-1">Job Seeker (Bright Green)</p>
                          <button
                            type="button"
                            onClick={() => setTempProfileFrame(tempProfileFrame === 'lookingtowork' ? '' : 'lookingtowork')}
                            className={`w-full px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                              tempProfileFrame === 'lookingtowork'
                                ? 'border-green-500 bg-green-50 text-green-900 font-bold shadow-[0_4px_12px_rgba(34,197,94,0.4)]'
                                : 'border-gray-200 hover:border-green-300 text-gray-700'
                            }`}
                            style={tempProfileFrame === 'lookingtowork' ? { borderColor: '#22c55e', backgroundColor: '#dcfce7' } : {}}
                          >
                            LOOKINGTOWORK
                          </button>
                          <button
                            type="button"
                            onClick={() => setTempProfileFrame(tempProfileFrame === 'exploringnewroles' ? '' : 'exploringnewroles')}
                            className={`w-full px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                              tempProfileFrame === 'exploringnewroles'
                                ? 'border-green-500 bg-green-50 text-green-900 font-bold shadow-[0_4px_12px_rgba(34,197,94,0.4)]'
                                : 'border-gray-200 hover:border-green-300 text-gray-700'
                            }`}
                            style={tempProfileFrame === 'exploringnewroles' ? { borderColor: '#22c55e', backgroundColor: '#dcfce7' } : {}}
                          >
                            EXPLORINGNEWROLES
                          </button>
                          <button
                            type="button"
                            onClick={() => setTempProfileFrame(tempProfileFrame === 'jobseeking' ? '' : 'jobseeking')}
                            className={`w-full px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                              tempProfileFrame === 'jobseeking'
                                ? 'border-green-600 bg-green-50 text-green-900 shadow-[0_4px_12px_rgba(34,197,94,0.4)]'
                                : 'border-gray-200 hover:border-green-300 text-gray-700'
                            }`}
                          >
                            JOBSEEKING
                          </button>
                        </div>
                        
                        {/* Hiring & Recruiting Options (Blue with Shadow) */}
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-600 mb-1">Hiring & Recruiting (Blue)</p>
                          <button
                            type="button"
                            onClick={() => setTempProfileFrame(tempProfileFrame === 'recruitingnow' ? '' : 'recruitingnow')}
                            className={`w-full px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                              tempProfileFrame === 'recruitingnow'
                                ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-[0_4px_12px_rgba(37,99,235,0.4)]'
                                : 'border-gray-200 hover:border-blue-300 text-gray-700'
                            }`}
                          >
                            RECRUITINGNOW
                          </button>
                          <button
                            type="button"
                            onClick={() => setTempProfileFrame(tempProfileFrame === 'seekingtalent' ? '' : 'seekingtalent')}
                            className={`w-full px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                              tempProfileFrame === 'seekingtalent'
                                ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-[0_4px_12px_rgba(37,99,235,0.4)]'
                                : 'border-gray-200 hover:border-blue-300 text-gray-700'
                            }`}
                          >
                            SEEKINGTALENT
                          </button>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Custom Text (Blue Frame)</label>
                            <input
                              type="text"
                              maxLength={16}
                              value={customFrameText}
                              onChange={(e) => {
                                const text = e.target.value.toUpperCase();
                                setCustomFrameText(text);
                                if (text) {
                                  setTempProfileFrame('custom:' + text);
                                } else {
                                  if (tempProfileFrame.startsWith('custom:')) {
                                    setTempProfileFrame('');
                                  }
                                }
                              }}
                              placeholder="16 char max"
                              className={`w-full px-3 py-2 text-sm rounded-lg border-2 transition-all uppercase font-medium ${
                                customFrameText
                                  ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-[0_4px_12px_rgba(37,99,235,0.4)]'
                                  : 'border-gray-200 hover:border-blue-300 text-gray-700'
                              }`}
                            />
                            <p className="text-xs text-gray-500 mt-1">{customFrameText.length}/16 characters</p>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTempProfileFrame('')}
                        className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        Remove frame
                      </button>
                    </div>
                  </div>
                </div>

                {/* Banner Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banner Image
                  </label>
                  <div className="space-y-4">
                    {/* Preview */}
                    {tempBannerUrl && (
                      <div className="relative h-32 rounded-lg overflow-hidden border border-gray-200">
                        <img
                          src={getPreviewUrl(tempBannerUrl)}
                          alt="Banner"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    
                    {/* File Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Upload Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleImageFileSelect('banner', file);
                          }
                          // Reset the input to allow re-uploading the same file or selecting a new file
                          e.target.value = '';
                        }}
                        className="input"
                      />
                    </div>

                    {/* URL Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Or Enter Image URL
                      </label>
                      <input
                        type="url"
                        value={typeof tempBannerUrl === 'string' ? tempBannerUrl : ''}
                        onChange={(e) => setTempBannerUrl(e.target.value)}
                        className="input"
                        placeholder="https://example.com/banner-image.jpg"
                      />
                      {tempBannerUrl instanceof Blob && (
                        <p className="text-xs text-gray-500 mt-1">File selected: {tempBannerUrl.name || 'uploaded-banner.jpg'}</p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {tempBannerUrl && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('Delete banner image?')) {
                              setTempBannerUrl('');
                            }
                          }}
                          className="btn btn-secondary text-sm inline-flex items-center gap-2 hover:bg-red-100 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Banner
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Background Border Color */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    Background Border Color (LinkedIn-style)
                  </label>
                  <div className="flex items-center gap-4 mb-4">
                    <div 
                      className="w-16 h-16 rounded-full border-4 border-gray-300 shadow-md"
                      style={{ 
                        backgroundColor: tempHighlightColor,
                        borderColor: tempHighlightColor
                      }}
                    ></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {HIGHLIGHT_COLORS.find(c => c.value === tempHighlightColor)?.name || 'Custom Color'}
                      </p>
                      <p className="text-xs text-gray-500">{tempHighlightColor}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 mb-4">
                    {HIGHLIGHT_COLORS.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setTempHighlightColor(color.value)}
                        className={`w-10 h-10 rounded-full border-2 transition-all ${
                          tempHighlightColor === color.value
                            ? 'border-gray-900 scale-110 ring-2 ring-gray-300'
                            : 'border-gray-300 hover:border-gray-500'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Color (Hex Code)
                    </label>
                    <input
                      type="text"
                      value={tempHighlightColor}
                      onChange={(e) => setTempHighlightColor(e.target.value)}
                      className="input"
                      placeholder="#3b82f6"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={handleCancelEditingImages}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveImages}
                  disabled={savingImages}
                  className="btn btn-primary"
                >
                  {savingImages ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container-custom pt-4 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8">
          {/* Left Column - Aligned with name/headline */}
          <div className="min-w-0">
            {/* Recovery notice / button (non-destructive) - HIDDEN per user request */}
            {/* {canEditProfile && (recoveryNote || (!profile.headline && !profile.summary && (!profile.experience || profile.experience.length === 0))) && (
              <div className="card mb-6 border border-amber-200 bg-amber-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-amber-900">Profile recovery</h3>
                    <p className="text-sm text-amber-800 mt-1">
                      {recoveryNote || 'If your profile looks empty, you can safely attempt recovery from cached data and the Resume entity. This never overwrites non-empty fields and creates a per-user backup first.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRecoverProfile}
                    disabled={recoveringProfile}
                    className="btn btn-outline text-sm px-3 py-1.5 flex-shrink-0"
                  >
                    {recoveringProfile ? 'Recovering…' : 'Recover profile data'}
                  </button>
                </div>
              </div>
            )} */}


            {/* Find People - Search for other users */}
            {isAuthenticated && !forceReadOnly && (
              <div className="card mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary-600" />
                  Find People
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Search by name or email to find other users and view their profiles.
                </p>
                {searchError && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {searchError}
                  </div>
                )}
                <form onSubmit={handleSearchPeople} className="flex flex-wrap gap-3 mb-4">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name or email..."
                      className="input w-full pl-9"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={searching}
                    className="btn btn-primary inline-flex items-center gap-2"
                  >
                    {searching ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4" />
                        Search
                      </>
                    )}
                  </button>
                </form>
                {searchResults.length > 0 && (
                  <div className="space-y-3">
                    <ul className="divide-y divide-gray-200">
                      {searchResults.map((r) => {
                        const pid = r.public_link_id;
                        const uid = r.profile_user_id;
                        const name = r.title || 'Unknown';
                        return (
                          <li key={r.id || r.public_link_id || r.profile_user_id} className="py-4 first:pt-0">
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0">
                                {r.photo_url ? (
                                  <img
                                    src={r.photo_url}
                                    alt={name}
                                    className="w-12 h-12 rounded-full object-cover bg-gray-100"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                                    <User className="w-6 h-6 text-primary-600" />
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-gray-900 truncate">{name}</p>
                                {r.headline && (
                                  <p className="text-sm text-gray-600 truncate">{r.headline}</p>
                                )}
                              </div>
                              <div className="flex-shrink-0 flex items-center gap-2">
                                {uid && (
                                  <button
                                    type="button"
                                    onClick={() => handleMessageUser(uid, "Hi! I'd like to connect.")}
                                    className="btn btn-primary text-sm px-3 py-1.5 inline-flex items-center gap-1"
                                    title="Message this user"
                                  >
                                    <MessageSquare className="w-3 h-3" />
                                    Message
                                  </button>
                                )}
                                {pid ? (
                                  <Link
                                    to={`/profile/view?public=${encodeURIComponent(pid)}&source=find_people`}
                                    className="btn btn-outline text-sm px-3 py-1.5 inline-flex items-center gap-1"
                                    title="View profile (same as View Profile button in their My Resumes section)"
                                  >
                                    View profile
                                    <ExternalLink className="w-3 h-3" />
                                  </Link>
                                ) : (
                                  <button
                                    type="button"
                                    disabled
                                    title="Profile not available - public link required"
                                    className="btn btn-outline text-sm px-3 py-1.5 inline-flex items-center gap-1 opacity-50 cursor-not-allowed"
                                  >
                                    View profile
                                  </button>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Debug Panel for Analytics */}
            {debugAnalytics && (
              <div className="mb-6 bg-gray-900 text-white rounded-lg p-6 font-mono text-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-green-400" />
                    <h3 className="text-lg font-bold text-green-400">Analytics Debug Panel</h3>
                  </div>
                  <button
                    onClick={() => {
                      setDebugLogs([]);
                      addDebugLog('Debug logs cleared', 'info');
                    }}
                    className="text-xs px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                  >
                    Clear Logs
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-800 p-3 rounded">
                    <div className="text-xs text-gray-400 mb-1">Profile Owner ID</div>
                    <div className="text-yellow-400">{profileOwnerId || 'Not set'}</div>
                  </div>
                  <div className="bg-gray-800 p-3 rounded">
                    <div className="text-xs text-gray-400 mb-1">Viewer ID</div>
                    <div className="text-yellow-400">{viewerId || 'Not set'}</div>
                  </div>
                  <div className="bg-gray-800 p-3 rounded">
                    <div className="text-xs text-gray-400 mb-1">Profile Views</div>
                    <div className="text-2xl font-bold text-blue-400">{analyticsData.profileViews.count}</div>
                  </div>
                  <div className="bg-gray-800 p-3 rounded">
                    <div className="text-xs text-gray-400 mb-1">Search Appearances</div>
                    <div className="text-2xl font-bold text-green-400">{analyticsData.searchAppearances.count}</div>
                  </div>
                </div>
                
                <div className="bg-gray-800 p-3 rounded mb-4">
                  <div className="text-xs text-gray-400 mb-2">Tracking Status</div>
                  <div className="text-sm">
                    {profileOwnerId === viewerId ? (
                      <div className="text-yellow-400">⚠️ Viewing own profile - tracking disabled</div>
                    ) : profile && profileOwnerId ? (
                      <div className="text-green-400">✅ Tracking active - viewing another user's profile</div>
                    ) : (
                      <div className="text-red-400">❌ Tracking inactive - profile not loaded</div>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-800 p-3 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-gray-400">Activity Log ({debugLogs.length})</div>
                    <button
                      onClick={async () => {
                        addDebugLog('Manual analytics refresh triggered', 'info');
                        setLoadingAnalytics(true);
                        try {
                          const AWS_API_BASE_URL = analyticsApiBaseUrl;
                          const token = getAnalyticsToken();
                          const awsUrl = (path) => {
                            const base = AWS_API_BASE_URL.replace(/\/+$/, '');
                            const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
                            return `${base}${p}`;
                          };
                          
                          const [viewsRes, searchesRes] = await Promise.all([
                            fetch(awsUrl('/me/analytics/profile-views?days=7&source=find_people&excludeConnected=true'), {
                              headers: { Authorization: `Bearer ${token}` }
                            }),
                            fetch(awsUrl('/me/analytics/search-appearances?days=7&source=find_people&excludeConnected=true'), {
                              headers: { Authorization: `Bearer ${token}` }
                            })
                          ]);
                          
                          const [views, searches] = await Promise.all([
                            viewsRes.ok ? viewsRes.json() : { views: [], count: 0, days: 7 },
                            searchesRes.ok ? searchesRes.json() : { count: 0, topQueries: [], days: 7 }
                          ]);
                          
                          setAnalyticsData(prev => ({
                            ...prev,
                            profileViews: views,
                            searchAppearances: searches
                          }));
                          
                          addDebugLog(`Analytics refreshed: ${views.count} views, ${searches.count} searches`, 'success');
                        } catch (err) {
                          addDebugLog(`Refresh failed: ${err.message}`, 'error');
                        } finally {
                          setLoadingAnalytics(false);
                        }
                      }}
                      className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-500 rounded transition-colors"
                    >
                      🔄 Refresh Analytics
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {debugLogs.length === 0 ? (
                      <div className="text-gray-500 text-xs">No activity yet...</div>
                    ) : (
                      debugLogs.map((log, i) => (
                        <div
                          key={i}
                          className={`text-xs p-2 rounded ${
                            log.type === 'error'
                              ? 'bg-red-900/30 text-red-300'
                              : log.type === 'success'
                              ? 'bg-green-900/30 text-green-300'
                              : 'bg-gray-700 text-gray-300'
                          }`}
                        >
                          <span className="text-gray-500">[{log.time}]</span> {log.message}
                        </div>
                      ))
                    )}
                  </div>
                </div>
                
                <div className="mt-3 text-xs text-gray-400">
                  💡 To disable debug mode, remove <code className="bg-gray-800 px-1 rounded">?debug=analytics</code> from URL
                </div>
              </div>
            )}

            {/* Analytics - Private to you (owner-only) */}
            {showOwnerOnlyDashboard && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-medium text-primary-600 uppercase tracking-wide">Private to you</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Eye className="w-6 h-6 text-primary-600" />
                Analytics
              </h2>
              
              {loadingAnalytics ? (
                <div className="text-center py-8 text-gray-500">
                  Loading analytics...
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ProfileViewsCard 
                    views={analyticsData.profileViews.views} 
                    count={analyticsData.profileViews.count}
                    days={analyticsData.profileViews.days}
                  />
                  <SearchAppearancesCard 
                    count={analyticsData.searchAppearances.count}
                    topQueries={analyticsData.searchAppearances.topQueries}
                    days={analyticsData.searchAppearances.days}
                  />
                </div>
              )}
            </div>
            )}

            {/* Summary - Always show if profile exists */}
            <div className="card mb-6 relative">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900">About</h2>
                {canEditProfile && (
                  <button
                    onClick={handleEditAbout}
                    className="text-gray-600 hover:text-gray-900 p-1 transition-colors"
                    title="Edit About section"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                )}
              </div>
              {profile.summary ? (
                <div>
                  <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap" style={{ lineHeight: '1.75', whiteSpace: 'pre-wrap' }}>
                    {aboutExpanded || profile.summary.length <= 300 ? (
                      profile.summary
                    ) : (
                      <>
                        {profile.summary.substring(0, 300)}
                        {profile.summary.length > 300 && '...'}
                      </>
                    )}
                  </div>
                  {profile.summary.length > 300 && (
                    <button
                      onClick={() => setAboutExpanded(!aboutExpanded)}
                      className="mt-2 text-primary-600 hover:text-primary-800 text-sm font-medium"
                    >
                      {aboutExpanded ? 'See less' : 'See more'}
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm italic">
                  No about information yet.{canEditProfile ? (
                    <> <Link to="/profile/edit" className="text-primary-600 hover:underline">Edit your profile</Link> to add information about yourself.</>
                  ) : null}
                </p>
              )}
            </div>

            {/* Credly Badges */}
            {credlyBadges.length > 0 && (
              <div className="card mb-6">
                <CredlyBadges badges={credlyBadges} onBadgesUpdate={setCredlyBadges} />
              </div>
            )}

            {/* Professional Summary - Always show if profile exists */}
            <div className="card mb-6 relative">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900">Professional Summary</h2>
                {canEditProfile && (
                  <button
                    onClick={handleEditProfessionalSummary}
                    className="text-gray-600 hover:text-gray-900 p-1 transition-colors"
                    title="Edit Professional Summary section"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                )}
              </div>
              {profile.professional_summary ? (
                <div className="text-gray-700 text-sm leading-relaxed">
                  {profile.professional_summary}
                </div>
              ) : (
                <p className="text-gray-500 text-sm italic">
                  No professional summary yet.{canEditProfile ? (
                    <> <Link to="/profile/edit" className="text-primary-600 hover:underline">Edit your profile</Link> to add a professional summary.</>
                  ) : null}
                </p>
              )}
            </div>

            {/* Experience */}
            <div className="card mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary-600" />
                Experience
              </h2>
              {profile.experience && profile.experience.length > 0 ? (
                <div className="space-y-8">
                  {experienceGroups.map((group, groupIndex) => {
                    const formatDate = (month, year) => {
                      if (!month || !year) return '';
                      return `${month} ${year}`;
                    };
                    const monthToIndex = { Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6, Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12 };
                    const toSortKey = (m, y) => {
                      if (!m || !y) return null;
                      const mi = typeof m === 'number' ? m : (monthToIndex[m] || parseInt(m, 10) || 0);
                      const yr = typeof y === 'string' ? parseInt(y, 10) : y;
                      return isNaN(yr) || !mi ? null : yr * 12 + mi;
                    };
                    let earliestStart = null;
                    let latestEnd = null;
                    let anyCurrent = false;
                    for (const { exp } of group.experiences) {
                      const sk = toSortKey(exp.start_month, exp.start_year);
                      if (sk != null && (earliestStart == null || sk < earliestStart.key)) {
                        earliestStart = { key: sk, month: exp.start_month, year: exp.start_year, fallback: exp.start_date };
                      }
                      if (exp.is_current) anyCurrent = true;
                      else {
                        const ek = toSortKey(exp.end_month, exp.end_year);
                        if (ek != null && (latestEnd == null || ek > latestEnd.key)) {
                          latestEnd = { key: ek, month: exp.end_month, year: exp.end_year, fallback: exp.end_date };
                        }
                      }
                    }
                    const firstStart = earliestStart
                      ? formatDate(earliestStart.month, earliestStart.year) || earliestStart.fallback || ''
                      : (group.experiences[0]?.exp?.start_date || '');
                    const lastEnd = anyCurrent
                      ? 'Present'
                      : (latestEnd
                        ? (formatDate(latestEnd.month, latestEnd.year) || latestEnd.fallback || '')
                        : '');
                    const orgDateRange = firstStart ? `${firstStart}${lastEnd ? ` - ${lastEnd}` : ''}` : '';
                    const logoIndex = group.experiences[0].index;

                    return (
                      <div key={groupIndex} className="relative">
                        {/* Org header: icon left by itself, text aligned with icon's left edge */}
                        <div className="relative mb-4">
                          {/* Icon positioned left by itself */}
                          <div className="absolute left-0 top-0">
                            <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                              {group.organization_logo ? (
                                <>
                                  <img src={group.organization_logo} alt={group.company ? `${group.company} logo` : 'Organization'} className="w-full h-full object-contain" />
                                  <input type="file" accept="image/*" className="hidden" id={`org-logo-replace-${logoIndex}`} disabled={uploadingLogo.expIndex === logoIndex}
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleOrganizationLogoUpload(logoIndex, f); e.target.value = ''; }} />
                                  <label htmlFor={`org-logo-replace-${logoIndex}`} className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-all" title="Click to replace logo">
                                    {uploadingLogo.expIndex === logoIndex && <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />}
                                  </label>
                                </>
                              ) : (
                                <>
                                  <input type="file" accept="image/*" className="hidden" id={`org-logo-upload-${logoIndex}`} disabled={uploadingLogo.expIndex === logoIndex}
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleOrganizationLogoUpload(logoIndex, f); e.target.value = ''; }} />
                                  <label htmlFor={`org-logo-upload-${logoIndex}`} className="cursor-pointer w-full h-full flex items-center justify-center hover:bg-gray-200 transition-colors" title="Click to upload organization logo">
                                    {uploadingLogo.expIndex === logoIndex ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" /> : <Briefcase className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400" />}
                                  </label>
                                </>
                              )}
                            </div>
                          </div>
                          {/* Org name and details shifted right by ~1 inch, icon stays in place */}
                          <div style={{ minHeight: '3.5rem', paddingLeft: '6rem' }}>
                            <p className="text-base font-bold text-gray-900">{group.company}</p>
                            {(orgDateRange || group.employment_type) && (
                              <p className="text-sm text-gray-600 mt-0.5">
                                {[group.employment_type, orgDateRange].filter(Boolean).join(' • ')}
                              </p>
                            )}
                            {group.location_type && (
                              <p className="text-sm text-gray-600">{group.location_type}</p>
                            )}
                          </div>
                        </div>
                        {/* Roles: dots on line + title, dates, location, description (see more), skills */}
                        <div className="space-y-5 relative" style={{ marginLeft: '6rem' }}>
                          {/* Vertical line from first dot to last dot - stops at last dot center */}
                          {group.experiences.length > 1 && (
                            <div 
                              className="absolute left-0 w-0.5 bg-gray-300" 
                              style={{ 
                                left: '-4.0625rem', 
                                top: '0.625rem',
                                height: 'calc(100% - 1.25rem)'
                              }}
                            ></div>
                          )}
                          {group.experiences.map(({ exp, index }, roleIndex) => {
                            const startDate = exp.start_month && exp.start_year ? formatDate(exp.start_month, exp.start_year) : exp.start_date || '';
                            const endDate = exp.is_current ? 'Present' : (exp.end_month && exp.end_year ? formatDate(exp.end_month, exp.end_year) : (exp.end_date && exp.end_date !== 'Present' ? exp.end_date : ''));
                            const dateRange = startDate ? `${startDate}${endDate ? ` - ${endDate}` : ''}` : '';
                            const skillsKey = `${groupIndex}-${index}`;
                            const skills = exp.skills || [];
                            // Show collapsed if more than 2 skills: show first 2, then "+X" where X = remaining (total - 2)
                            // "+18" means 2 shown + 18 remaining = 20 total, which is >= 19 ✓
                            const showCollapsed = skills.length > 2 && !expandedSkills[skillsKey];
                            const displayedSkills = showCollapsed ? skills.slice(0, 2) : skills;
                            const remainingCount = skills.length - 2;
                            
                            return (
                              <div key={index} className="flex gap-3 relative pr-8">
                                {/* Dot on the vertical line - only show if multiple roles in same org */}
                                {group.experiences.length > 1 && (
                                  <div className="absolute w-2 h-2 rounded-full bg-gray-400" style={{ left: '-4.25rem', top: '0.375rem' }} aria-hidden />
                                )}
                                <div className="flex-1 min-w-0">
                                  {/* Edit button on the far right */}
                                  {canEditProfile && (
                                    <button
                                      onClick={() => handleEditExperience(index)}
                                      className="absolute right-0 top-0 text-gray-600 hover:text-gray-900 p-1 transition-colors"
                                      title="Edit this work experience"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                  )}
                                  <p className="text-base font-semibold text-gray-900">{exp.title}</p>
                                  <div className="text-sm text-gray-600 mt-0.5 space-y-0.5">
                                    {dateRange && <div>{dateRange}</div>}
                                    {exp.location && <div>{exp.location}</div>}
                                  </div>
                                  {exp.description && (
                                    <div className="mt-2">
                                      <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap" style={{ lineHeight: '1.75' }}>
                                        {expandedExperiences[index] || exp.description.length <= 300 ? exp.description : (
                                          <>{exp.description.substring(0, 300)}{exp.description.length > 300 && '...'}</>
                                        )}
                                      </p>
                                      {exp.description.length > 300 && (
                                        <button onClick={() => setExpandedExperiences(prev => ({ ...prev, [index]: !prev[index] }))} className="mt-1 text-primary-600 hover:text-primary-800 text-sm font-medium">
                                          {expandedExperiences[index] ? 'See less' : 'See more'}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  {skills.length > 0 && (
                                    <div className="mt-2">
                                      <div className="flex flex-wrap items-center gap-2">
                                        {displayedSkills.map((s, i) => (
                                          <span key={i} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">{s}</span>
                                        ))}
                                        {showCollapsed && remainingCount > 0 && (
                                          <button
                                            onClick={() => setExpandedSkills(prev => ({ ...prev, [skillsKey]: true }))}
                                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                                          >
                                            and +{remainingCount} skills
                                          </button>
                                        )}
                                        {expandedSkills[skillsKey] && remainingCount > 0 && (
                                          <>
                                            {skills.slice(2).map((s, i) => (
                                              <span key={i + 2} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">{s}</span>
                                            ))}
                                            <button
                                              onClick={() => setExpandedSkills(prev => ({ ...prev, [skillsKey]: false }))}
                                              className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                                            >
                                              Show less
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {exp.media && exp.media.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                      {exp.media.map((m, i) => (
                                        <div key={i} className="text-sm">
                                          {m.type === 'image' && m.url && <img src={m.url} alt={m.title || 'Media'} className="max-w-xs rounded-lg mb-1" />}
                                          {m.title && <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">{m.title}</a>}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {exp.achievements && exp.achievements.length > 0 && (
                                    <ul className="list-disc list-inside text-gray-700 text-sm mt-2 space-y-1">
                                      {exp.achievements.map((a, i) => <li key={i}>{a}</li>)}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No work experience added yet.</p>
                  {canEditProfile && (
                    <Link to="/profile/edit" className="text-primary-600 hover:underline mt-2 inline-block">
                      Add your work experience
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:w-80 flex-shrink-0">
            {/* Languages */}
            {profile.languages && profile.languages.length > 0 && (
              <div className="card mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Languages className="w-5 h-5 text-primary-600" />
                  Languages
                </h3>
                <div className="space-y-2">
                  {profile.languages.map((lang, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{lang.name || lang}</span>
                      <span className="text-sm text-gray-600">{lang.proficiency || 'Proficient'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {profile.education && profile.education.length > 0 && (
              <div className="card mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-primary-600" />
                  Education
                </h2>
                <div className="space-y-8">
                  {profile.education.map((edu, index) => {
                    const formatDate = (month, year) => {
                      if (!month || !year) return '';
                      return `${month} ${year}`;
                    };
                    const startDate = edu.start_month && edu.start_year ? formatDate(edu.start_month, edu.start_year) : (edu.start_date || '');
                    const endDate = edu.is_expected ? 'Expected' : (edu.end_month && edu.end_year ? formatDate(edu.end_month, edu.end_year) : (edu.end_date && edu.end_date !== 'Present' ? edu.end_date : ''));
                    const dateRange = startDate ? `${startDate}${endDate ? ` - ${endDate}` : ''}` : '';

                    return (
                      <div key={index} className="relative">
                        {/* Institution header: icon left by itself, text aligned with icon's left edge */}
                        <div className="relative mb-4">
                          {/* Icon positioned left by itself */}
                          <div className="absolute left-0 top-0">
                            <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                              {edu.institution_logo ? (
                                <>
                                  <img src={edu.institution_logo} alt={edu.institution ? `${edu.institution} logo` : 'Institution'} className="w-full h-full object-contain" />
                                  <input type="file" accept="image/*" className="hidden" id={`edu-logo-replace-${index}`} disabled={uploadingEduLogo.eduIndex === index}
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleEducationLogoUpload(index, f); e.target.value = ''; }} />
                                  <label htmlFor={`edu-logo-replace-${index}`} className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-all" title="Click to replace logo">
                                    {uploadingEduLogo.eduIndex === index && <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />}
                                  </label>
                                </>
                              ) : (
                                <>
                                  <input type="file" accept="image/*" className="hidden" id={`edu-logo-upload-${index}`} disabled={uploadingEduLogo.eduIndex === index}
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleEducationLogoUpload(index, f); e.target.value = ''; }} />
                                  <label htmlFor={`edu-logo-upload-${index}`} className="cursor-pointer w-full h-full flex items-center justify-center hover:bg-gray-200 transition-colors" title="Click to upload institution logo">
                                    {uploadingEduLogo.eduIndex === index ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" /> : <GraduationCap className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400" />}
                                  </label>
                                </>
                              )}
                            </div>
                          </div>
                          {/* Institution name and details shifted right by ~1 inch, icon stays in place */}
                          <div style={{ minHeight: '3.5rem', paddingLeft: '6rem' }}>
                            <p className="text-base font-bold text-gray-900">{edu.institution || 'Institution'}</p>
                            {dateRange && (
                              <p className="text-sm text-gray-600 mt-0.5">
                                {dateRange}
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Education details: degree, field of study, location, GPA, description, activities */}
                        <div className="space-y-5 relative" style={{ marginLeft: '6rem' }}>
                          <div className="flex gap-3 relative pr-8">
                            <div className="flex-1 min-w-0">
                              {/* Edit button on the far right */}
                              {canEditProfile && (
                                <button
                                  onClick={() => handleEditEducation(index)}
                                  className="absolute right-0 top-0 text-gray-600 hover:text-gray-900 p-1 transition-colors"
                                  title="Edit this education"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              )}
                              {edu.degree && (
                                <p className="text-base font-semibold text-gray-900">{edu.degree}</p>
                              )}
                              {edu.field_of_study && (
                                <p className="text-sm text-gray-600 mt-0.5">{edu.field_of_study}</p>
                              )}
                              <div className="text-sm text-gray-600 mt-0.5 space-y-0.5">
                                {edu.location && <div>{edu.location}</div>}
                                {edu.gpa && <div>GPA: {edu.gpa}</div>}
                              </div>
                              {edu.description && (
                                <div className="mt-2">
                                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap" style={{ lineHeight: '1.75' }}>
                                    {expandedEducation[index] || edu.description.length <= 300 ? edu.description : (
                                      <>{edu.description.substring(0, 300)}{edu.description.length > 300 && '...'}</>
                                    )}
                                  </p>
                                  {edu.description.length > 300 && (
                                    <button onClick={() => setExpandedEducation(prev => ({ ...prev, [index]: !prev[index] }))} className="mt-1 text-primary-600 hover:text-primary-800 text-sm font-medium">
                                      {expandedEducation[index] ? 'See less' : 'See more'}
                                    </button>
                                  )}
                                </div>
                              )}
                              {edu.activities_and_societies && (
                                <div className="mt-2">
                                  <p className="text-sm text-gray-600 font-medium">Activities and societies</p>
                                  <p className="text-gray-700 text-sm mt-0.5">{edu.activities_and_societies}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Certifications */}
            {profile.certifications && profile.certifications.filter(c => c.display_on_profile !== false).length > 0 && (
              <div className="card mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Award className="w-6 h-6 text-primary-600" />
                  Certifications
                </h2>
                <div className="space-y-3">
                  {profile.certifications.filter(c => c.display_on_profile !== false).map((cert, index) => (
                    <div key={index} className="flex items-start relative pr-8">
                      {/* Certification Logo/Icon - Clickable for upload */}
                      <div className="relative w-10 h-10 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 mr-3 mt-0.5">
                        {cert.certification_logo ? (
                          <>
                            <img src={cert.certification_logo} alt={cert.name ? `${cert.name} logo` : 'Certification'} className="w-full h-full object-contain" />
                            <input type="file" accept="image/*" className="hidden" id={`cert-logo-replace-${index}`} disabled={uploadingCertLogo.certIndex === index}
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCertificationLogoUpload(index, f); e.target.value = ''; }} />
                            <label htmlFor={`cert-logo-replace-${index}`} className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-all" title="Click to replace logo">
                              {uploadingCertLogo.certIndex === index && <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />}
                            </label>
                          </>
                        ) : (
                          <>
                            <input type="file" accept="image/*" className="hidden" id={`cert-logo-upload-${index}`} disabled={uploadingCertLogo.certIndex === index}
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCertificationLogoUpload(index, f); e.target.value = ''; }} />
                            <label htmlFor={`cert-logo-upload-${index}`} className="cursor-pointer w-full h-full flex items-center justify-center hover:bg-gray-200 transition-colors" title="Click to upload certification logo">
                              {uploadingCertLogo.certIndex === index ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" /> : <Award className="w-5 h-5 text-gray-400" />}
                            </label>
                          </>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{cert.name}</p>
                        <p className="text-sm text-gray-600">
                          {cert.issuer}
                          {(cert.issue_month && cert.issue_year) && ` • ${cert.issue_month} ${cert.issue_year}`}
                          {!cert.issue_month && !cert.issue_year && cert.date && ` • ${cert.date}`}
                          {(cert.expiration_month && cert.expiration_year) && ` - ${cert.expiration_month} ${cert.expiration_year}`}
                        </p>
                        {cert.credential_id && (
                          <p className="text-xs text-gray-500 mt-1">ID: {cert.credential_id}</p>
                        )}
                        {cert.credential_url && (
                          <a
                            href={cert.credential_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-block"
                          >
                            <button className="px-3 py-1 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors">
                              Show credential
                            </button>
                          </a>
                        )}
                        {cert.skills && cert.skills.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {cert.skills.map((skill, skillIndex) => (
                              <span key={skillIndex} className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Edit button on the far right */}
                      {canEditProfile && (
                        <button
                          onClick={() => handleEditCertification(index)}
                          className="absolute right-0 top-0 text-gray-600 hover:text-gray-900 p-1 transition-colors"
                          title="Edit this certification"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Training & Professional Development */}
            {profile.training && profile.training.length > 0 && (
              <div className="card mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-primary-600" />
                  Training & Professional Development
                </h2>
                <div className="space-y-4">
                  {profile.training.map((training, index) => (
                    <div key={index} className="border-l-4 border-primary-200 pl-4">
                      <h3 className="text-base font-semibold text-gray-900">{training.name}</h3>
                      <p className="text-primary-600 font-medium mb-1 text-sm">{training.provider}</p>
                      <p className="text-sm text-gray-600 mb-2">
                        {training.start_date}{training.end_date ? ` - ${training.end_date}` : ' - Present'}
                        {training.location && ` • ${training.location}`}
                      </p>
                      {training.description && (
                        <p className="text-gray-700 text-sm leading-relaxed">{training.description}</p>
                      )}
                      {training.certificate_url && (
                        <a
                          href={training.certificate_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline text-sm mt-2 inline-flex items-center gap-1"
                        >
                          View Certificate <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Analytics Cards - Vertical Layout (Owner only) */}
            {showOwnerOnlyDashboard && (
              <div className="grid grid-cols-1 gap-4 mb-6">
                {/* Who Your Viewers Also Viewed */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-base">Who Your Viewers Also Viewed</h3>
                      <p className="text-sm text-gray-500">Similar professionals</p>
                    </div>
                  </div>
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-500">No viewer connection data yet</p>
                  </div>
                </div>

                {/* People from Your Company */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-base">People from Your Company</h3>
                      <p className="text-sm text-gray-500"></p>
                    </div>
                  </div>
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-500">No colleagues from your company found</p>
                  </div>
                </div>
              </div>
            )}


            {/* Projects - Hidden for now */}
            {false && profile.projects && profile.projects.length > 0 && (
              <div className="card mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Code className="w-6 h-6 text-primary-600" />
                  Projects
                </h2>
                <div className="space-y-4">
                  {profile.projects.map((project, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{project.name}</h3>
                      {project.url && (
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline text-sm mb-2 inline-block"
                        >
                          View Project →
                        </a>
                      )}
                      {project.description && (
                        <p className="text-gray-700 text-sm">{project.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Experience Modal */}
      {editingExperience !== null && editingExpData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCancelEditExperience}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Edit Work Experience</h2>
              <button
                onClick={handleCancelEditExperience}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingExpData.title || ''}
                  onChange={(e) => updateEditingExpField('title', e.target.value)}
                  className="input w-full"
                  placeholder="Ex: Retail Sales Manager"
                  required
                />
              </div>

              {/* Employment Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employment type
                </label>
                <select
                  value={editingExpData.employment_type || ''}
                  onChange={(e) => updateEditingExpField('employment_type', e.target.value)}
                  className="select w-full"
                >
                  <option value="">Please select</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Temporary">Temporary</option>
                  <option value="Internship">Internship</option>
                  <option value="Volunteer">Volunteer</option>
                  <option value="Freelance">Freelance</option>
                </select>
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company or organization <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingExpData.company || ''}
                  onChange={(e) => updateEditingExpField('company', e.target.value)}
                  className="input w-full"
                  placeholder="Ex: Microsoft"
                  required
                />
              </div>

              {/* Currently Working */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingExpData.is_current || false}
                  onChange={(e) => {
                    updateEditingExpField('is_current', e.target.checked);
                    if (e.target.checked) {
                      updateEditingExpField('end_month', '');
                      updateEditingExpField('end_year', '');
                    }
                  }}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                  id="edit-current"
                />
                <label htmlFor="edit-current" className="ml-2 text-sm font-medium text-gray-700">
                  I am currently working in this role
                </label>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start date <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Month</label>
                    <select
                      value={editingExpData.start_month || ''}
                      onChange={(e) => updateEditingExpField('start_month', e.target.value)}
                      className="select w-full"
                      required
                    >
                      <option value="">Month</option>
                      <option value="Jan">January</option>
                      <option value="Feb">February</option>
                      <option value="Mar">March</option>
                      <option value="Apr">April</option>
                      <option value="May">May</option>
                      <option value="Jun">June</option>
                      <option value="Jul">July</option>
                      <option value="Aug">August</option>
                      <option value="Sep">September</option>
                      <option value="Oct">October</option>
                      <option value="Nov">November</option>
                      <option value="Dec">December</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Year</label>
                    <select
                      value={editingExpData.start_year || ''}
                      onChange={(e) => updateEditingExpField('start_year', e.target.value)}
                      className="select w-full"
                      required
                    >
                      <option value="">Year</option>
                      {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* End Date */}
              {!editingExpData.is_current && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End date <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Month</label>
                      <select
                        value={editingExpData.end_month || ''}
                        onChange={(e) => updateEditingExpField('end_month', e.target.value)}
                        className="select w-full"
                        required={!editingExpData.is_current}
                      >
                        <option value="">Month</option>
                        <option value="Jan">January</option>
                        <option value="Feb">February</option>
                        <option value="Mar">March</option>
                        <option value="Apr">April</option>
                        <option value="May">May</option>
                        <option value="Jun">June</option>
                        <option value="Jul">July</option>
                        <option value="Aug">August</option>
                        <option value="Sep">September</option>
                        <option value="Oct">October</option>
                        <option value="Nov">November</option>
                        <option value="Dec">December</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Year</label>
                      <select
                        value={editingExpData.end_year || ''}
                        onChange={(e) => updateEditingExpField('end_year', e.target.value)}
                        className="select w-full"
                        required={!editingExpData.is_current}
                      >
                        <option value="">Year</option>
                        {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={editingExpData.location || ''}
                  onChange={(e) => updateEditingExpField('location', e.target.value)}
                  className="input w-full"
                  placeholder="Ex: London, United Kingdom"
                />
              </div>

              {/* Location Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location type
                </label>
                <select
                  value={editingExpData.location_type || ''}
                  onChange={(e) => updateEditingExpField('location_type', e.target.value)}
                  className="select w-full"
                >
                  <option value="">Please select</option>
                  <option value="On-site">On-site</option>
                  <option value="Remote">Remote</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editingExpData.description || ''}
                  onChange={(e) => updateEditingExpField('description', e.target.value)}
                  className="textarea w-full"
                  rows="6"
                  placeholder="List your major duties and successes, highlighting specific projects"
                  maxLength={2500}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {(editingExpData.description || '').length}/2500 characters
                </p>
              </div>

              {/* Skills */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Skills
                </label>
                <div className="space-y-2">
                  {(editingExpData.skills || []).map((skill, skillIndex) => (
                    <div key={skillIndex} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={skill || ''}
                        onChange={(e) => {
                          const newSkills = [...(editingExpData.skills || [])];
                          newSkills[skillIndex] = e.target.value;
                          updateEditingExpField('skills', newSkills);
                        }}
                        className="input flex-1"
                        placeholder="Add skill"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newSkills = (editingExpData.skills || []).filter((_, i) => i !== skillIndex);
                          updateEditingExpField('skills', newSkills);
                        }}
                        className="text-red-600 hover:text-red-800 p-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newSkills = [...(editingExpData.skills || []), ''];
                      updateEditingExpField('skills', newSkills);
                    }}
                    className="btn btn-outline text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add skill
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={handleCancelEditExperience}
                className="btn btn-outline"
                disabled={savingExperience}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveExperience}
                className="btn btn-primary"
                disabled={savingExperience || !editingExpData.title || !editingExpData.company}
              >
                {savingExperience ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit About Modal */}
      {canEditProfile && editingAbout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCancelEditAbout}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Edit About</h2>
              <button
                onClick={handleCancelEditAbout}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  About
                </label>
                <textarea
                  value={editingAboutText}
                  onChange={(e) => setEditingAboutText(e.target.value)}
                  className="textarea w-full"
                  rows="10"
                  placeholder="Write about yourself for your profile page..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This appears in the "About" section on your profile page.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={handleCancelEditAbout}
                className="btn btn-outline"
                disabled={savingAbout}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAbout}
                className="btn btn-primary"
                disabled={savingAbout}
              >
                {savingAbout ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Professional Summary Modal */}
      {canEditProfile && editingProfessionalSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCancelEditProfessionalSummary}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Edit Professional Summary</h2>
              <button
                onClick={handleCancelEditProfessionalSummary}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Professional Summary
                </label>
                <textarea
                  value={editingProfessionalSummaryText}
                  onChange={(e) => setEditingProfessionalSummaryText(e.target.value)}
                  className="textarea w-full"
                  rows="10"
                  placeholder="Write a professional summary that will appear above your work experience on your profile page..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This appears above the Work Experience section on your profile page.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={handleCancelEditProfessionalSummary}
                className="btn btn-outline"
                disabled={savingProfessionalSummary}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfessionalSummary}
                className="btn btn-primary"
                disabled={savingProfessionalSummary}
              >
                {savingProfessionalSummary ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Education Modal */}
      {editingEducation !== null && editingEduData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCancelEditEducation}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Edit Education</h2>
              <button
                onClick={handleCancelEditEducation}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Institution */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Institution Name
                </label>
                <input
                  type="text"
                  value={editingEduData.institution || ''}
                  onChange={(e) => updateEditingEduField('institution', e.target.value)}
                  className="input w-full"
                  placeholder="Ex: Harvard University"
                />
              </div>

              {/* Degree */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Degree
                </label>
                <input
                  type="text"
                  value={editingEduData.degree || ''}
                  onChange={(e) => updateEditingEduField('degree', e.target.value)}
                  className="input w-full"
                  placeholder="Ex: Bachelor of Science in Computer Science"
                />
              </div>

              {/* Field of Study */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Field of study
                </label>
                <input
                  type="text"
                  value={editingEduData.field_of_study || ''}
                  onChange={(e) => updateEditingEduField('field_of_study', e.target.value)}
                  className="input w-full"
                  placeholder="Ex: Computer Science"
                />
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start date
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Month</label>
                    <select
                      value={editingEduData.start_month || ''}
                      onChange={(e) => updateEditingEduField('start_month', e.target.value)}
                      className="select w-full"
                    >
                      <option value="">Month</option>
                      <option value="Jan">January</option>
                      <option value="Feb">February</option>
                      <option value="Mar">March</option>
                      <option value="Apr">April</option>
                      <option value="May">May</option>
                      <option value="Jun">June</option>
                      <option value="Jul">July</option>
                      <option value="Aug">August</option>
                      <option value="Sep">September</option>
                      <option value="Oct">October</option>
                      <option value="Nov">November</option>
                      <option value="Dec">December</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Year</label>
                    <select
                      value={editingEduData.start_year || ''}
                      onChange={(e) => updateEditingEduField('start_year', e.target.value)}
                      className="select w-full"
                    >
                      <option value="">Year</option>
                      {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* End Date (or Expected) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End date {editingEduData.is_expected ? '(or expected)' : ''}
                </label>
                <div className="mb-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingEduData.is_expected || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        updateEditingEduField('is_expected', e.target.checked);
                        if (e.target.checked) {
                          updateEditingEduField('end_month', '');
                          updateEditingEduField('end_year', '');
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 mr-2 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 cursor-pointer" onClick={(e) => {
                      e.stopPropagation();
                      updateEditingEduField('is_expected', !editingEduData.is_expected);
                      if (!editingEduData.is_expected) {
                        updateEditingEduField('end_month', '');
                        updateEditingEduField('end_year', '');
                      }
                    }}>This is an expected end date</span>
                  </label>
                </div>
                {!editingEduData.is_expected && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Month</label>
                      <select
                        value={editingEduData.end_month || ''}
                        onChange={(e) => updateEditingEduField('end_month', e.target.value)}
                        className="select w-full"
                      >
                        <option value="">Month</option>
                        <option value="Jan">January</option>
                        <option value="Feb">February</option>
                        <option value="Mar">March</option>
                        <option value="Apr">April</option>
                        <option value="May">May</option>
                        <option value="Jun">June</option>
                        <option value="Jul">July</option>
                        <option value="Aug">August</option>
                        <option value="Sep">September</option>
                        <option value="Oct">October</option>
                        <option value="Nov">November</option>
                        <option value="Dec">December</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Year</label>
                      <select
                        value={editingEduData.end_year || ''}
                        onChange={(e) => updateEditingEduField('end_year', e.target.value)}
                        className="select w-full"
                      >
                        <option value="">Year</option>
                        {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {editingEduData.is_expected && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Month (or expected)</label>
                      <select
                        value={editingEduData.end_month || ''}
                        onChange={(e) => updateEditingEduField('end_month', e.target.value)}
                        className="select w-full"
                      >
                        <option value="">Month</option>
                        <option value="Jan">January</option>
                        <option value="Feb">February</option>
                        <option value="Mar">March</option>
                        <option value="Apr">April</option>
                        <option value="May">May</option>
                        <option value="Jun">June</option>
                        <option value="Jul">July</option>
                        <option value="Aug">August</option>
                        <option value="Sep">September</option>
                        <option value="Oct">October</option>
                        <option value="Nov">November</option>
                        <option value="Dec">December</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Year (or expected)</label>
                      <select
                        value={editingEduData.end_year || ''}
                        onChange={(e) => updateEditingEduField('end_year', e.target.value)}
                        className="select w-full"
                      >
                        <option value="">Year</option>
                        {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() + i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={editingEduData.location || ''}
                  onChange={(e) => updateEditingEduField('location', e.target.value)}
                  className="input w-full"
                  placeholder="Ex: Cambridge, MA"
                />
              </div>

              {/* GPA */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GPA
                </label>
                <input
                  type="text"
                  value={editingEduData.gpa || ''}
                  onChange={(e) => updateEditingEduField('gpa', e.target.value)}
                  className="input w-full"
                  placeholder="Ex: 3.8"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editingEduData.description || ''}
                  onChange={(e) => updateEditingEduField('description', e.target.value)}
                  className="textarea w-full"
                  rows="6"
                  placeholder="Add any relevant details about your education"
                />
              </div>

              {/* Activities and Societies */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activities and societies
                </label>
                <input
                  type="text"
                  value={editingEduData.activities_and_societies || ''}
                  onChange={(e) => updateEditingEduField('activities_and_societies', e.target.value)}
                  className="input w-full"
                  placeholder="Ex: Student Council, Debate Team, Honor Society"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={handleCancelEditEducation}
                className="btn btn-outline"
                disabled={savingEducation}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEducation}
                className="btn btn-primary"
                disabled={savingEducation}
              >
                {savingEducation ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Certification Modal */}
      {editingCertification !== null && editingCertData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCancelEditCertification}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Edit license or certification</h2>
              <button
                onClick={handleCancelEditCertification}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-xs text-gray-500 mb-4">* Indicates required</p>

              {/* Name - Required */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingCertData.name || ''}
                  onChange={(e) => updateEditingCertField('name', e.target.value)}
                  className="input w-full border border-gray-300 rounded"
                  placeholder="e.g., Project Management Professional (PMP)"
                />
              </div>

              {/* Issuing organization - Required */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issuing organization <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingCertData.issuer || ''}
                  onChange={(e) => updateEditingCertField('issuer', e.target.value)}
                  className="input w-full border border-gray-300 rounded"
                  placeholder="e.g., Project Management Institute"
                />
              </div>

              {/* Issue date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issue date
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <select
                      value={editingCertData.issue_month || ''}
                      onChange={(e) => updateEditingCertField('issue_month', e.target.value)}
                      className="input w-full border border-gray-300 rounded"
                    >
                      <option value="">Month</option>
                      <option value="January">January</option>
                      <option value="February">February</option>
                      <option value="March">March</option>
                      <option value="April">April</option>
                      <option value="May">May</option>
                      <option value="June">June</option>
                      <option value="July">July</option>
                      <option value="August">August</option>
                      <option value="September">September</option>
                      <option value="October">October</option>
                      <option value="November">November</option>
                      <option value="December">December</option>
                    </select>
                  </div>
                  <div>
                    <select
                      value={editingCertData.issue_year || ''}
                      onChange={(e) => updateEditingCertField('issue_year', e.target.value)}
                      className="input w-full border border-gray-300 rounded"
                    >
                      <option value="">Year</option>
                      {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Expiration date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration date
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <select
                      value={editingCertData.expiration_month || ''}
                      onChange={(e) => updateEditingCertField('expiration_month', e.target.value)}
                      className="input w-full border border-gray-300 rounded"
                    >
                      <option value="">Month</option>
                      <option value="January">January</option>
                      <option value="February">February</option>
                      <option value="March">March</option>
                      <option value="April">April</option>
                      <option value="May">May</option>
                      <option value="June">June</option>
                      <option value="July">July</option>
                      <option value="August">August</option>
                      <option value="September">September</option>
                      <option value="October">October</option>
                      <option value="November">November</option>
                      <option value="December">December</option>
                    </select>
                  </div>
                  <div>
                    <select
                      value={editingCertData.expiration_year || ''}
                      onChange={(e) => updateEditingCertField('expiration_year', e.target.value)}
                      className="input w-full border border-gray-300 rounded"
                    >
                      <option value="">Year</option>
                      {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() + i).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Credential ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credential ID
                </label>
                <input
                  type="text"
                  value={editingCertData.credential_id || ''}
                  onChange={(e) => updateEditingCertField('credential_id', e.target.value)}
                  className="input w-full border border-gray-300 rounded"
                  placeholder="e.g., ABC123-XYZ789"
                />
              </div>

              {/* Credential URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credential URL
                </label>
                <input
                  type="url"
                  value={editingCertData.credential_url || ''}
                  onChange={(e) => updateEditingCertField('credential_url', e.target.value)}
                  className="input w-full border border-gray-300 rounded"
                  placeholder="https://www.credly.com/badges/..."
                />
              </div>

              {/* Skills Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Skills
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  Associate at least 1 skill to this license or certification. It'll also appear in your Skills section.
                </p>
                <div className="space-y-2">
                  {(editingCertData.skills || []).map((skill, skillIndex) => (
                    <div key={skillIndex} className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const newSkills = (editingCertData.skills || []).filter((_, i) => i !== skillIndex);
                            updateEditingCertField('skills', newSkills);
                          }}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        {skill}
                      </span>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const skillName = prompt('Enter skill name:');
                      if (skillName && skillName.trim()) {
                        const newSkills = [...(editingCertData.skills || []), skillName.trim()];
                        updateEditingCertField('skills', newSkills);
                      }
                    }}
                    className="btn btn-outline text-sm inline-flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add skill
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={handleCancelEditCertification}
                className="btn btn-outline"
                disabled={savingCertification}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCertification}
                className="btn btn-primary"
                disabled={savingCertification || !editingCertData.name || !editingCertData.issuer}
              >
                {savingCertification ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Editor Modal */}
      <ImageEditorModal
        isOpen={showImageEditor}
        onClose={() => {
          setShowImageEditor(false);
          setEditingImageType(null);
        }}
        imageUrl={editingImageType === 'photo' ? getPreviewUrl(tempPhotoUrl) : getPreviewUrl(tempBannerUrl)}
        onSave={(editedData) => {
          // Store the CloudFront URL returned from S3 upload
          if (editingImageType === 'photo') {
            setTempPhotoUrl(editedData.imageUrl);
          } else if (editingImageType === 'banner') {
            setTempBannerUrl(editedData.imageUrl);
          }
          setShowImageEditor(false);
          setEditingImageType(null);
        }}
        title={editingImageType === 'photo' ? 'Edit profile photo' : 'Edit banner image'}
        imageType={editingImageType === 'photo' ? 'avatar' : 'banner'}
      />
    </div>
  );
};

export default ProfilePage;
